import { useState, useEffect, useCallback, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Toaster, toast } from "sonner";
import { 
  FileText, Copy, Download, RefreshCw, Zap, Terminal, Hash, Phone, Globe, 
  FileStack, Sparkles, Type, HelpCircle, LogIn, LogOut, User, Shield, 
  CreditCard, Moon, Sun, ChevronRight, Check, X, Users, Ban, DollarSign,
  Plus, Trash2, Search, Lock, Unlock, Gift, Wallet, ArrowUpRight, Clock, Share2,
  UserPlus, Key, ArrowLeft, Palette, Eye, ExternalLink
} from "lucide-react";
import LandingPageEnhanced from "@/components/LandingPageEnhanced";
import AdminPanelNew from "@/components/AdminPanelNew";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Theme Context
const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

const LANGUAGES = [
  { code: "en", name: "English" },
  { code: "es", name: "Español" },
  { code: "fr", name: "Français" },
  { code: "de", name: "Deutsch" },
  { code: "pt", name: "Português" },
];

const INITIAL_KEYWORDS = Array(10).fill("");
const INITIAL_FAQS = [
  { question: "", answer: "" },
  { question: "", answer: "" },
  { question: "", answer: "" },
  { question: "", answer: "" },
  { question: "", answer: "" },
];

// ==================== LANDING PAGE ====================
function LandingPage({ onGetStarted, theme, toggleTheme }) {
  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-[#09090b] text-white' : 'bg-white text-gray-900'}`}>
      {/* Header */}
      <header className={`border-b ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'} p-4`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#ccff00] flex items-center justify-center">
              <Terminal className="w-6 h-6 text-black" />
            </div>
            <span className="font-bold text-xl tracking-tight">ContentWriter.World</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-gray-500/20">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <Button onClick={onGetStarted} className="bg-[#ccff00] text-black hover:bg-[#ccff00]/90">
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section - Futuristic 3D */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden hero-gradient hero-grid">
        {/* Scan Line Effect */}
        <div className="scan-line"></div>
        
        {/* Particle Background */}
        <div className="particles-container">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${15 + Math.random() * 10}s`
              }}
            />
          ))}
        </div>
        
        {/* 3D Background Text */}
        <div className="text-3d-container">
          <div className="text-3d">WRITE CONTENT</div>
        </div>
        
        {/* Road Track */}
        <div className="road-track"></div>
        
        {/* Racing Car Animation */}
        <div className="racing-car-container">
          {/* Speed Lines */}
          <div className="speed-lines">
            <div className="speed-line"></div>
            <div className="speed-line"></div>
            <div className="speed-line"></div>
          </div>
          
          {/* Exhaust Smoke */}
          <div className="exhaust-smoke">
            <div className="smoke-puff"></div>
            <div className="smoke-puff"></div>
            <div className="smoke-puff"></div>
          </div>
          
          {/* Racing Car SVG */}
          <div className="racing-car">
            <svg viewBox="0 0 200 80" xmlns="http://www.w3.org/2000/svg">
              {/* Car Body */}
              <path 
                className="car-body" 
                d="M30 50 L50 50 L55 35 L75 25 L140 25 L160 35 L175 50 L180 50 L180 60 L20 60 L20 50 Z"
              />
              {/* Car Body Dark Shade */}
              <path 
                className="car-body-dark" 
                d="M25 55 L180 55 L180 60 L20 60 L20 55 Z"
              />
              {/* Windows */}
              <path 
                className="car-window" 
                d="M60 35 L72 28 L120 28 L130 35 Z"
              />
              {/* Front Light */}
              <rect className="car-light" x="170" y="45" width="8" height="6" rx="1"/>
              {/* Tail Light */}
              <rect className="car-tail-light" x="22" y="45" width="6" height="8" rx="1"/>
              {/* Racing Stripes */}
              <rect fill="#ffffff" x="70" y="35" width="60" height="3" opacity="0.8"/>
              <rect fill="#ffffff" x="75" y="40" width="50" height="2" opacity="0.5"/>
              {/* Front Wheel */}
              <circle className="car-wheel" cx="55" cy="60" r="14"/>
              <circle className="car-wheel-rim" cx="55" cy="60" r="8"/>
              <circle fill="#333" cx="55" cy="60" r="4"/>
              {/* Rear Wheel */}
              <circle className="car-wheel" cx="150" cy="60" r="14"/>
              <circle className="car-wheel-rim" cx="150" cy="60" r="8"/>
              <circle fill="#333" cx="150" cy="60" r="4"/>
              {/* Spoiler */}
              <path fill="#cc0000" d="M20 40 L10 30 L15 30 L25 40 Z"/>
              {/* Number */}
              <text x="95" y="48" fill="#fff" fontSize="12" fontWeight="bold" textAnchor="middle">01</text>
            </svg>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="relative z-10 max-w-5xl mx-auto text-center px-4">
          {/* Floating Badge */}
          <div className="badge-float inline-block px-6 py-2 mb-8 text-sm font-mono bg-[#ccff00]/10 text-[#ccff00] border border-[#ccff00]/30 backdrop-blur-sm">
            <span className="inline-block w-2 h-2 bg-[#ccff00] rounded-full mr-2 animate-pulse"></span>
            AI-POWERED SEO GENERATOR
          </div>
          
          {/* Main Heading */}
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black mb-6 tracking-tight leading-none">
            <span className="block mb-2">Generate SEO Content</span>
            <span className="text-[#ccff00] glow-text">In Seconds</span>
            <span className="typing-cursor"></span>
          </h1>
          
          {/* Subheading */}
          <p className={`text-lg md:text-xl mb-10 max-w-2xl mx-auto ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Create keyword-rich content with <span className="text-[#ccff00]">fancy phone numbers</span>, 
            <span className="text-[#ccff00]"> colorful emojis</span>, and 
            <span className="text-[#ccff00]"> Unicode characters</span>. 
            Export to PDF instantly.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              onClick={onGetStarted} 
              size="lg" 
              className="btn-glow bg-[#ccff00] text-black hover:bg-[#ccff00] text-lg px-10 py-7 font-bold tracking-wide"
            >
              Start Generating <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
            <a 
              href="#pricing" 
              className={`px-8 py-4 border ${theme === 'dark' ? 'border-[#3f3f46] hover:border-[#ccff00]' : 'border-gray-300 hover:border-[#ccff00]'} transition-colors font-medium`}
            >
              View Pricing
            </a>
          </div>
          
          {/* Stats */}
          <div className="mt-16 grid grid-cols-3 gap-8 max-w-lg mx-auto">
            {[
              { value: "100+", label: "Pages/PDF" },
              { value: "10", label: "Keywords" },
              { value: "5+", label: "Languages" },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-black text-[#ccff00] glow-text">{stat.value}</div>
                <div className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Bottom Gradient Fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#09090b] to-transparent"></div>
      </section>

      {/* Features */}
      <section className={`py-16 px-4 ${theme === 'dark' ? 'bg-[#18181b]' : 'bg-gray-50'}`}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Powerful Features</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Hash, title: "10 Keywords", desc: "Add up to 10 keywords for diverse content generation" },
              { icon: Phone, title: "Fancy Numbers", desc: "Unicode, emojis, and decorative phone formats" },
              { icon: FileText, title: "PDF Export", desc: "Export up to 100 pages with colorful emojis" },
              { icon: HelpCircle, title: "Auto FAQ", desc: "Auto-generate or add custom FAQs" },
              { icon: Globe, title: "Multi-Language", desc: "Support for EN, ES, FR, DE, PT" },
              { icon: CreditCard, title: "Crypto Payment", desc: "Pay with BNB, SOL, USDT, ETH" },
            ].map((feature, i) => (
              <div key={i} className={`p-6 ${theme === 'dark' ? 'bg-[#27272a] border-[#3f3f46]' : 'bg-white border-gray-200'} border`}>
                <feature.icon className="w-10 h-10 text-[#ccff00] mb-4" />
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">Pricing Plans</h2>
          <p className={`text-center mb-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Pay with cryptocurrency - BNB, SOL, USDT (BEP20), ETH
          </p>
          
          {/* Pricing Plans */}
          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {/* Monthly Plan */}
            <div className={`p-6 ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-gray-50 border-gray-200'} border rounded-xl`}>
              <h4 className="text-lg font-bold mb-2">Monthly</h4>
              <div className="text-3xl font-black text-[#ccff00] mb-3">$280<span className="text-sm font-normal">/mo</span></div>
              <ul className="space-y-2 mb-4 text-sm">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#ccff00]" /> Full access to all features</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#ccff00]" /> Unlimited PDF generation</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#ccff00]" /> Priority support</li>
              </ul>
              <Button onClick={onGetStarted} className="w-full bg-[#ccff00] text-black text-sm">Get Monthly</Button>
            </div>
            {/* Lifetime Plan */}
            <div className={`p-6 ${theme === 'dark' ? 'bg-[#18181b] border-[#ccff00]' : 'bg-gray-50 border-[#ccff00]'} border-2 rounded-xl relative`}>
              <div className="absolute -top-3 left-4 px-2 py-1 bg-[#ccff00] text-black text-xs font-bold rounded">BEST VALUE</div>
              <h4 className="text-lg font-bold mb-2">Lifetime</h4>
              <div className="text-3xl font-black text-[#ccff00] mb-3">$750<span className="text-sm font-normal"> once</span></div>
              <ul className="space-y-2 mb-4 text-sm">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#ccff00]" /> One-time payment</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#ccff00]" /> Forever access</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#ccff00]" /> VIP support</li>
              </ul>
              <Button onClick={onGetStarted} className="w-full bg-[#ccff00] text-black text-sm">Get Lifetime</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className={`py-12 px-4 ${theme === 'dark' ? 'bg-[#18181b]' : 'bg-gray-50'}`}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-6">Contact Us</h2>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <a 
              href="https://wa.me/919217512367" 
              target="_blank" 
              rel="noopener noreferrer"
              className={`flex items-center gap-3 px-6 py-3 ${theme === 'dark' ? 'bg-[#27272a] hover:bg-[#3f3f46]' : 'bg-white hover:bg-gray-100'} border ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'} transition-colors`}
            >
              <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <span className="font-bold">+91 9217512367</span>
            </a>
            <a 
              href="https://t.me/indexing_king" 
              target="_blank" 
              rel="noopener noreferrer"
              className={`flex items-center gap-3 px-6 py-3 ${theme === 'dark' ? 'bg-[#27272a] hover:bg-[#3f3f46]' : 'bg-white hover:bg-gray-100'} border ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'} transition-colors`}
            >
              <svg className="w-6 h-6 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
              </svg>
              <span className="font-bold">@indexing_king</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-8 px-4 border-t ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}>
              © 2024 ContentWriter.World. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <a href="https://wa.me/919217512367" target="_blank" rel="noopener noreferrer" className={`${theme === 'dark' ? 'text-gray-400 hover:text-green-500' : 'text-gray-500 hover:text-green-600'} transition-colors`}>
                WhatsApp: +91 9217512367
              </a>
              <span className={theme === 'dark' ? 'text-gray-600' : 'text-gray-300'}>|</span>
              <a href="https://t.me/indexing_king" target="_blank" rel="noopener noreferrer" className={`${theme === 'dark' ? 'text-gray-400 hover:text-blue-500' : 'text-gray-500 hover:text-blue-600'} transition-colors`}>
                Telegram: @indexing_king
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ==================== DEVICE FINGERPRINT ====================
function getDeviceFingerprint() {
  // Create a simple device fingerprint based on browser properties
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('device-fp', 2, 2);
  const canvasData = canvas.toDataURL();
  
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    new Date().getTimezoneOffset(),
    canvasData.slice(-50)
  ].join('|');
  
  // Create a hash
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return 'fp-' + Math.abs(hash).toString(16);
}

function getDeviceName() {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome')) return 'Chrome Browser';
  if (ua.includes('Firefox')) return 'Firefox Browser';
  if (ua.includes('Safari')) return 'Safari Browser';
  if (ua.includes('Edge')) return 'Edge Browser';
  if (ua.includes('Mobile')) return 'Mobile Device';
  return 'Unknown Browser';
}

// ==================== AUTH PAGE ====================
function AuthPage({ onLogin, theme, toggleTheme }) {
  const [activationCode, setActivationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [credits, setCredits] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(`${API}/quickindex/login`, { 
        activation_code: activationCode.trim()
      });
      
      if (res.data.success) {
        // Store activation code for subsequent requests
        localStorage.setItem("activation_code", activationCode.trim());
        localStorage.setItem("quickindex_credits", res.data.credits);
        localStorage.setItem("quickindex_email", res.data.user_email || "");
        
        toast.success(`Welcome! You have ${res.data.credits} credits`);
        onLogin();
      } else {
        toast.error(res.data.error || "Login failed");
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Invalid activation code";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-[#09090b]' : 'bg-gray-100'}`}>
      <div className="absolute top-4 right-4">
        <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-gray-500/20">
          {theme === 'dark' ? <Sun className="w-5 h-5 text-white" /> : <Moon className="w-5 h-5" />}
        </button>
      </div>
      
      <div className={`w-full max-w-md p-8 ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border rounded-xl`}>
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-12 h-12 bg-[#ccff00] flex items-center justify-center rounded-lg">
            <Terminal className="w-7 h-7 text-black" />
          </div>
          <span className={`font-bold text-2xl ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>ContentWriter.World</span>
        </div>

        <div className="text-center mb-6">
          <h2 className={`text-xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Login with Activation Key
          </h2>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Enter your QuickIndex.Pro activation code
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Activation Code</Label>
            <Input
              type="text"
              value={activationCode}
              onChange={(e) => setActivationCode(e.target.value)}
              placeholder="Enter your activation code"
              className={`mt-1 text-center text-lg tracking-wider ${theme === 'dark' ? 'bg-[#27272a] border-[#3f3f46] text-white' : ''}`}
              required
              data-testid="activation-code-input"
            />
          </div>
          
          <Button 
            type="submit" 
            data-testid="auth-submit-btn" 
            className="w-full bg-[#ccff00] text-black font-bold py-3" 
            disabled={loading || !activationCode.trim()}
          >
            {loading ? <RefreshCw className="animate-spin" /> : "Login"}
          </Button>
        </form>

        <div className={`mt-6 p-4 rounded-lg ${theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-100'}`}>
          <p className={`text-xs text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Get your activation code from{' '}
            <a 
              href="https://quickindex.pro" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#ccff00] hover:underline font-bold"
            >
              QuickIndex.Pro
            </a>
            {' '}→ Profile → Activation Code
          </p>
        </div>
      </div>
    </div>
  );
}

// ==================== PAYMENT PAGE ====================
function PaymentPage({ user, onSuccess, theme }) {
  const [pricing, setPricing] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState("monthly");
  const [selectedCurrency, setSelectedCurrency] = useState("usdtbsc");
  const [paymentData, setPaymentData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState("waiting");
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const [timeUntilNextCheck, setTimeUntilNextCheck] = useState(60);

  // Check if user has had a subscription before (not first-time)
  const hasHadSubscription = user?.subscription_type || user?.subscription_status === "expired" || user?.had_subscription;
  const isFirstTimeUser = !hasHadSubscription;

  const plans = [
    { id: "monthly", label: "Monthly (30 Days)", price: 280, type: "monthly", maxUsers: 1 },
    { id: "lifetime", label: "Lifetime (One-Time)", price: 750, type: "lifetime", maxUsers: 1 },
  ];

  const statusLabels = {
    waiting: { label: "Waiting for payment", color: "text-yellow-500", bg: "bg-yellow-500/10" },
    confirming: { label: "Confirming...", color: "text-blue-500", bg: "bg-blue-500/10" },
    confirmed: { label: "Confirmed!", color: "text-green-500", bg: "bg-green-500/10" },
    sending: { label: "Processing...", color: "text-blue-500", bg: "bg-blue-500/10" },
    partially_paid: { label: "Partial payment received", color: "text-orange-500", bg: "bg-orange-500/10" },
    finished: { label: "Payment Complete!", color: "text-green-500", bg: "bg-green-500/10" },
    failed: { label: "Payment failed", color: "text-red-500", bg: "bg-red-500/10" },
    refunded: { label: "Refunded", color: "text-gray-500", bg: "bg-gray-500/10" },
    expired: { label: "Expired", color: "text-red-500", bg: "bg-red-500/10" },
  };

  useEffect(() => {
    fetchPricing();
  }, []);

  // Poll for payment status every 60 seconds
  useEffect(() => {
    if (!paymentData?.payment_id) return;
    
    // Initial check
    checkPaymentStatus();
    
    // Set up polling interval (60 seconds)
    const pollInterval = setInterval(() => {
      checkPaymentStatus();
    }, 60000);
    
    // Countdown timer
    const countdownInterval = setInterval(() => {
      setTimeUntilNextCheck(prev => {
        if (prev <= 1) return 60;
        return prev - 1;
      });
    }, 1000);
    
    return () => {
      clearInterval(pollInterval);
      clearInterval(countdownInterval);
    };
  }, [paymentData?.payment_id]);

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/payments/pricing`);
      setPricing(res.data);
    } catch (error) {
      console.error("Failed to fetch pricing");
    }
  };

  const checkPaymentStatus = async () => {
    if (!paymentData?.payment_id || checkingStatus) return;
    
    setCheckingStatus(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API}/payments/status/${paymentData.payment_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPaymentStatus(res.data.status);
      setLastChecked(new Date());
      setTimeUntilNextCheck(60);
      
      // If payment is complete, show success and redirect
      if (res.data.subscription_activated) {
        toast.success("🎉 Payment confirmed! Subscription activated!");
        setTimeout(() => {
          onSuccess && onSuccess();
        }, 2000);
      }
    } catch (error) {
      console.error("Failed to check payment status:", error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const createPayment = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/payments/create`,
        { plan_type: selectedPlan, currency: selectedCurrency },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPaymentData(res.data);
      setPaymentStatus("waiting");
      setTimeUntilNextCheck(60);
      toast.success("Payment created! Send crypto to the address below.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment");
    } finally {
      setLoading(false);
    }
  };

  const selectedPlanData = plans.find(p => p.id === selectedPlan);
  const currentStatus = statusLabels[paymentStatus] || statusLabels.waiting;
  const [qrTab, setQrTab] = useState("address"); // "address" or "amount"

  // Get currency display info
  const getCurrencyBadge = (currency) => {
    const badges = {
      bnbbsc: { name: "BNB", network: "BSC", color: "bg-yellow-500" },
      usdtbsc: { name: "USDT", network: "BSC", color: "bg-green-500" },
      sol: { name: "SOL", network: "SOL", color: "bg-purple-500" },
      eth: { name: "ETH", network: "ETH", color: "bg-blue-500" },
    };
    return badges[currency] || { name: currency?.toUpperCase(), network: "", color: "bg-gray-500" };
  };

  // Status steps for stepper
  const statusSteps = [
    { key: "waiting", label: "Awaiting deposit", icon: Clock },
    { key: "confirming", label: "Confirming", icon: RefreshCw },
    { key: "sending", label: "Processing", icon: Zap },
    { key: "finished", label: "Complete", icon: Check },
  ];

  const getStepStatus = (stepKey) => {
    const stepOrder = ["waiting", "confirming", "sending", "finished"];
    const currentIndex = stepOrder.indexOf(paymentStatus === "confirmed" ? "finished" : paymentStatus);
    const stepIndex = stepOrder.indexOf(stepKey);
    
    if (stepIndex < currentIndex) return "completed";
    if (stepIndex === currentIndex) return "active";
    return "pending";
  };

  return (
    <div className={`min-h-screen p-4 md:p-8 ${theme === 'dark' ? 'bg-[#09090b] text-white' : 'bg-gray-100'}`}>
      <div className="max-w-lg mx-auto">
        {!paymentData ? (
          <>
            <h1 className="text-2xl font-bold mb-6 text-center">
              <CreditCard className="inline w-6 h-6 mr-2 text-[#ccff00]" />
              Subscribe to ContentWriter.World
            </h1>
            <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white'} border`}>
              <div className="mb-6">
                <Label className="text-sm font-bold uppercase mb-2 block">Select Plan</Label>
                <select
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  className={`w-full p-3 border rounded-lg ${theme === 'dark' ? 'bg-[#27272a] border-[#3f3f46] text-white' : 'bg-white border-gray-200'}`}
                >
                  <option value="monthly">Monthly - $280/mo</option>
                  <option value="lifetime">Lifetime - $750 (One-Time)</option>
                </select>
                <div className="mt-2 text-center">
                  <span className="text-2xl font-black text-[#ccff00]">${selectedPlanData?.price}</span>
                  <span className="text-sm text-gray-400 ml-2">{selectedPlanData?.label}</span>
                </div>
              </div>

              <div className="mb-6">
                <Label className="text-sm font-bold uppercase mb-2 block">Select Cryptocurrency</Label>
                <div className="grid grid-cols-2 gap-3">
                  {pricing?.supported_currencies?.map((curr) => (
                    <button
                      key={curr.code}
                      onClick={() => setSelectedCurrency(curr.code)}
                      className={`p-3 border-2 rounded-lg text-left transition-all ${selectedCurrency === curr.code ? "border-[#ccff00] bg-[#ccff00]/10" : theme === 'dark' ? "border-[#3f3f46] hover:border-[#52525b]" : "border-gray-200 hover:border-gray-300"}`}
                    >
                      {curr.name}
                    </button>
                  ))}
                </div>
              </div>

              <Button onClick={createPayment} className="w-full bg-[#ccff00] text-black hover:bg-[#b8e600] font-bold" disabled={loading}>
                {loading ? <RefreshCw className="animate-spin mr-2" /> : <CreditCard className="mr-2" />}
                Create Payment
              </Button>
            </div>
          </>
        ) : (
          <div className={`rounded-xl overflow-hidden ${theme === 'dark' ? 'bg-[#18181b]' : 'bg-white'} border ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'}`}>
            {/* Transaction ID Header */}
            <div className={`px-4 py-3 flex items-center justify-between ${theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-50'} border-b ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'}`}>
              <span className="text-sm text-gray-500">Transaction ID</span>
              <div className="flex items-center gap-2">
                <code className={`px-2 py-1 rounded text-sm ${theme === 'dark' ? 'bg-[#3f3f46]' : 'bg-gray-200'}`}>
                  {paymentData.order_id?.slice(0, 16) || paymentData.payment_id}
                </code>
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(paymentData.order_id || paymentData.payment_id);
                    toast.success("Transaction ID copied!");
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Main Content */}
            <div className="p-6">
              {/* Amount Section */}
              <div className={`p-4 rounded-lg mb-6 ${theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-50'}`}>
                <div className="text-sm text-gray-400 mb-1">Amount</div>
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-bold">{paymentData.pay_amount}</span>
                  <span className="text-2xl font-bold text-gray-400">{getCurrencyBadge(paymentData.pay_currency).name}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${getCurrencyBadge(paymentData.pay_currency).color}`}>
                    {getCurrencyBadge(paymentData.pay_currency).network}
                  </span>
                </div>
                <div className="text-sm text-gray-500 mt-1">≈ ${paymentData.price_usd} USD</div>
              </div>

              {/* Address Section */}
              <div className="mb-6">
                <div className="text-sm text-gray-400 mb-2">To this address</div>
                <div className="flex items-start gap-2">
                  <code className={`flex-1 p-3 rounded-lg font-mono text-sm break-all ${theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-50'}`}>
                    {paymentData.pay_address}
                  </code>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(paymentData.pay_address);
                      toast.success("Address copied!");
                    }}
                    className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-[#27272a] hover:bg-[#3f3f46]' : 'bg-gray-50 hover:bg-gray-100'}`}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* QR Code Tabs */}
              <div className="mb-4">
                <div className="flex rounded-lg overflow-hidden border ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'}">
                  <button
                    onClick={() => setQrTab("address")}
                    className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${qrTab === "address" ? 'bg-[#ccff00] text-black' : theme === 'dark' ? 'bg-[#27272a] text-gray-400' : 'bg-gray-50 text-gray-600'}`}
                  >
                    Address
                  </button>
                  <button
                    onClick={() => setQrTab("amount")}
                    className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${qrTab === "amount" ? 'bg-[#ccff00] text-black' : theme === 'dark' ? 'bg-[#27272a] text-gray-400' : 'bg-gray-50 text-gray-600'}`}
                  >
                    With amount
                  </button>
                </div>
              </div>

              {/* QR Code */}
              <div className="flex justify-center mb-6">
                <div className="bg-white p-4 rounded-xl relative">
                  <QRCodeSVG 
                    value={qrTab === "amount" ? `${paymentData.pay_address}?amount=${paymentData.pay_amount}` : paymentData.pay_address} 
                    size={200}
                    level="H"
                    includeMargin={true}
                    fgColor="#000000"
                    bgColor="#ffffff"
                  />
                  {/* Currency logo overlay */}
                  <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center ${getCurrencyBadge(paymentData.pay_currency).color}`}>
                    <span className="text-white font-bold text-xs">{getCurrencyBadge(paymentData.pay_currency).name}</span>
                  </div>
                </div>
              </div>

              {/* Status Stepper */}
              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-50'}`}>
                <div className="space-y-4">
                  {statusSteps.map((step, index) => {
                    const status = getStepStatus(step.key);
                    const StepIcon = step.icon;
                    return (
                      <div key={step.key} className="flex items-center gap-4">
                        <div className="relative">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            status === "completed" ? "bg-green-500" :
                            status === "active" ? "bg-[#ccff00]" :
                            theme === 'dark' ? "bg-[#3f3f46]" : "bg-gray-200"
                          }`}>
                            {status === "completed" ? (
                              <Check className="w-4 h-4 text-white" />
                            ) : status === "active" ? (
                              <StepIcon className={`w-4 h-4 text-black ${step.key === "waiting" || step.key === "confirming" ? "animate-pulse" : ""}`} />
                            ) : (
                              <div className="w-2 h-2 rounded-full bg-gray-400" />
                            )}
                          </div>
                          {index < statusSteps.length - 1 && (
                            <div className={`absolute top-8 left-1/2 transform -translate-x-1/2 w-0.5 h-6 ${
                              status === "completed" ? "bg-green-500" : theme === 'dark' ? "bg-[#3f3f46]" : "bg-gray-200"
                            }`} />
                          )}
                        </div>
                        <span className={`text-sm font-medium ${
                          status === "active" ? "text-[#ccff00]" :
                          status === "completed" ? "text-green-500" :
                          "text-gray-500"
                        }`}>
                          {step.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Auto-refresh info */}
              <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                <span>Auto-checking every 60s</span>
                <button 
                  onClick={checkPaymentStatus}
                  disabled={checkingStatus}
                  className="flex items-center gap-1 text-[#ccff00] hover:underline"
                >
                  <RefreshCw className={`w-3 h-3 ${checkingStatus ? "animate-spin" : ""}`} />
                  {checkingStatus ? "Checking..." : `Check now (${timeUntilNextCheck}s)`}
                </button>
              </div>

              {/* Warning */}
              <div className={`mt-4 p-3 rounded-lg ${theme === 'dark' ? 'bg-orange-500/10 border-orange-500/30' : 'bg-orange-50 border-orange-200'} border`}>
                <p className="text-sm text-orange-500 text-center">
                  ⚠️ Send only <strong>{getCurrencyBadge(paymentData.pay_currency).name}</strong> on <strong>{getCurrencyBadge(paymentData.pay_currency).network}</strong> network
                </p>
              </div>

              {/* Create New Payment */}
              <Button 
                onClick={() => {
                  setPaymentData(null);
                  setPaymentStatus("waiting");
                }} 
                variant="outline" 
                className={`w-full mt-4 ${theme === 'dark' ? 'border-[#3f3f46]' : ''}`}
              >
                Create New Payment
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ==================== REFERRAL DASHBOARD ====================
function ReferralDashboard({ theme, onBack }) {
  const [stats, setStats] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawMethod, setWithdrawMethod] = useState("usdt");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [walletAddress, setWalletAddress] = useState("");
  const [upiAddress, setUpiAddress] = useState("");

  useEffect(() => {
    fetchStats();
    fetchWithdrawals();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/referral/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      toast.error("Failed to fetch referral stats");
    }
  };

  const fetchWithdrawals = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/referral/withdrawals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWithdrawals(res.data.withdrawals);
    } catch (error) {
      console.error("Failed to fetch withdrawals");
    }
  };

  const copyReferralLink = () => {
    if (stats?.referral_code) {
      const link = `${window.location.origin}?ref=${stats.referral_code}`;
      navigator.clipboard.writeText(link);
      toast.success("Referral link copied!");
    }
  };

  const copyReferralCode = () => {
    if (stats?.referral_code) {
      navigator.clipboard.writeText(stats.referral_code);
      toast.success("Referral code copied!");
    }
  };

  const shareToWhatsApp = () => {
    if (stats?.referral_code) {
      const link = `${window.location.origin}?ref=${stats.referral_code}`;
      const text = `Join ContentWriter.World using my referral link and get amazing SEO content tools! ${link}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  const shareToTelegram = () => {
    if (stats?.referral_code) {
      const link = `${window.location.origin}?ref=${stats.referral_code}`;
      const text = `Join ContentWriter.World using my referral link and get amazing SEO content tools!`;
      window.open(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  const shareToTwitter = () => {
    if (stats?.referral_code) {
      const link = `${window.location.origin}?ref=${stats.referral_code}`;
      const text = `Generate SEO content in seconds with ContentWriter.World! Join using my referral link:`;
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(link)}`, '_blank');
    }
  };

  const requestWithdrawal = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/referral/withdraw`, {
        amount: parseFloat(withdrawAmount),
        method: withdrawMethod,
        wallet_address: withdrawMethod === "usdt" ? walletAddress : null,
        upi_address: withdrawMethod === "inr" ? upiAddress : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Withdrawal request submitted!");
      setShowWithdraw(false);
      setWithdrawAmount("");
      setWalletAddress("");
      setUpiAddress("");
      fetchStats();
      fetchWithdrawals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Withdrawal failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen p-8 ${theme === 'dark' ? 'bg-[#09090b] text-white' : 'bg-gray-100'}`}>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">
            <Gift className="inline w-8 h-8 mr-2 text-[#ccff00]" />
            Referral Dashboard
          </h1>
          <Button onClick={onBack} variant="outline" className={theme === 'dark' ? 'border-[#3f3f46]' : ''}>
            Back
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className={`p-4 ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border`}>
              <div className="text-2xl font-bold text-[#ccff00]">{stats.total_referrals}</div>
              <div className="text-sm text-gray-400">Total Referrals</div>
            </div>
            <div className={`p-4 ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border`}>
              <div className="text-2xl font-bold text-orange-500">{stats.pending_referrals || 0}</div>
              <div className="text-sm text-gray-400">Pending 1st Purchase</div>
            </div>
            <div className={`p-4 ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border`}>
              <div className="text-2xl font-bold text-green-500">{stats.successful_referrals}</div>
              <div className="text-sm text-gray-400">Paid Referrals</div>
            </div>
            <div className={`p-4 ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border`}>
              <div className="text-2xl font-bold text-[#ccff00]">${stats.commission_balance?.toFixed(2) || "0.00"}</div>
              <div className="text-sm text-gray-400">Available Balance</div>
            </div>
          </div>
        )}

        {/* Referral Code & Link */}
        <div className={`p-6 mb-6 ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border`}>
          {/* Referral Code Section */}
          <div className="mb-6">
            <h2 className="font-bold mb-3">Your Referral Code</h2>
            <div className="flex gap-2 items-center">
              <div className={`flex-1 px-4 py-3 font-mono text-xl font-bold tracking-wider ${theme === 'dark' ? 'bg-[#27272a] border-[#3f3f46]' : 'bg-gray-100'} border text-[#ccff00]`}>
                {stats?.referral_code || "Loading..."}
              </div>
              <Button onClick={copyReferralCode} className="bg-[#ccff00] text-black">
                <Copy className="w-4 h-4 mr-2" /> Copy Code
              </Button>
            </div>
          </div>

          {/* Referral Link Section */}
          <div className="mb-6">
            <h2 className="font-bold mb-3">Your Referral Link</h2>
            <div className="flex gap-2">
              <Input
                readOnly
                value={stats ? `${window.location.origin}?ref=${stats.referral_code}` : "Loading..."}
                className={`flex-1 ${theme === 'dark' ? 'bg-[#27272a] border-[#3f3f46]' : ''}`}
              />
              <Button onClick={copyReferralLink} className="bg-[#ccff00] text-black">
                <Copy className="w-4 h-4 mr-2" /> Copy Link
              </Button>
            </div>
          </div>

          {/* Share Buttons */}
          <div className="mb-4">
            <h3 className="font-bold mb-3">Share Your Link</h3>
            <div className="flex flex-wrap gap-3">
              <Button onClick={shareToWhatsApp} className="bg-green-600 hover:bg-green-700 text-white">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </Button>
              <Button onClick={shareToTelegram} className="bg-blue-500 hover:bg-blue-600 text-white">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                Telegram
              </Button>
              <Button onClick={shareToTwitter} className="bg-black hover:bg-gray-800 text-white border border-gray-600">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                Twitter/X
              </Button>
            </div>
          </div>

          {/* Commission Info */}
          <p className="text-sm text-gray-400 mt-4">
            Earn commissions for every referral who purchases:
          </p>
          <div className="flex gap-4 mt-2">
            <div className="text-center">
              <div className="text-[#ccff00] font-bold">$20</div>
              <div className="text-xs text-gray-500">Monthly ($280)</div>
            </div>
            <div className="text-center">
              <div className="text-[#ccff00] font-bold">$50</div>
              <div className="text-xs text-gray-500">Lifetime ($750)</div>
            </div>
          </div>
        </div>

        {/* Withdraw Button */}
        <div className="mb-6">
          <Button 
            onClick={() => setShowWithdraw(true)} 
            className="w-full bg-[#ccff00] text-black"
            disabled={!stats || stats.commission_balance <= 0}
          >
            <Wallet className="w-4 h-4 mr-2" />
            Withdraw Balance (${stats?.commission_balance?.toFixed(2) || "0.00"})
          </Button>
        </div>

        {/* Withdraw Modal */}
        {showWithdraw && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`w-full max-w-md ${theme === 'dark' ? 'bg-[#18181b]' : 'bg-white'} p-6`}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Request Withdrawal</h2>
                <Button variant="ghost" onClick={() => setShowWithdraw(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="mb-4">
                <Label className="text-sm font-bold mb-2 block">Amount (USD)</Label>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  max={stats?.commission_balance || 0}
                  className={theme === 'dark' ? 'bg-[#27272a] border-[#3f3f46]' : ''}
                />
                <p className="text-xs text-gray-400 mt-1">Available: ${stats?.commission_balance?.toFixed(2)}</p>
              </div>

              <div className="mb-4">
                <Label className="text-sm font-bold mb-2 block">Withdrawal Method</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setWithdrawMethod("usdt")}
                    className={`p-3 border-2 ${withdrawMethod === "usdt" ? "border-[#ccff00] bg-[#ccff00]/10" : theme === 'dark' ? "border-[#3f3f46]" : "border-gray-200"}`}
                  >
                    USDT (BEP20)
                  </button>
                  <button
                    onClick={() => setWithdrawMethod("inr")}
                    className={`p-3 border-2 ${withdrawMethod === "inr" ? "border-[#ccff00] bg-[#ccff00]/10" : theme === 'dark' ? "border-[#3f3f46]" : "border-gray-200"}`}
                  >
                    INR (UPI)
                  </button>
                </div>
              </div>

              {withdrawMethod === "usdt" && (
                <div className="mb-4">
                  <Label className="text-sm font-bold mb-2 block">BEP20 Wallet Address</Label>
                  <Input
                    placeholder="0x..."
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                    className={theme === 'dark' ? 'bg-[#27272a] border-[#3f3f46]' : ''}
                  />
                </div>
              )}

              {withdrawMethod === "inr" && (
                <div className="mb-4">
                  <Label className="text-sm font-bold mb-2 block">UPI Address</Label>
                  <Input
                    placeholder="yourname@upi"
                    value={upiAddress}
                    onChange={(e) => setUpiAddress(e.target.value)}
                    className={theme === 'dark' ? 'bg-[#27272a] border-[#3f3f46]' : ''}
                  />
                </div>
              )}

              <Button onClick={requestWithdrawal} className="w-full bg-[#ccff00] text-black" disabled={loading}>
                {loading ? <RefreshCw className="animate-spin mr-2" /> : <ArrowUpRight className="mr-2 w-4 h-4" />}
                Submit Withdrawal Request
              </Button>
            </div>
          </div>
        )}

        {/* Withdrawal History */}
        <div className={`p-6 ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border`}>
          <h2 className="font-bold mb-4">Withdrawal History</h2>
          {withdrawals.length > 0 ? (
            <div className="space-y-2">
              {withdrawals.map((w) => (
                <div key={w.id} className={`p-3 ${theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-100'} flex justify-between items-center`}>
                  <div>
                    <div className="font-bold">${w.amount.toFixed(2)}</div>
                    <div className="text-xs text-gray-400">
                      {w.method.toUpperCase()} - {new Date(w.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs ${
                    w.status === "approved" ? "bg-green-500/20 text-green-500" :
                    w.status === "rejected" ? "bg-red-500/20 text-red-500" :
                    "bg-orange-500/20 text-orange-500"
                  }`}>
                    {w.status.toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center">No withdrawal history</p>
          )}
        </div>

        {/* Referrals List */}
        {stats?.referrals?.length > 0 && (
          <div className={`mt-6 p-6 ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border`}>
            <h2 className="font-bold mb-4">Your Referrals</h2>
            <div className="space-y-2">
              {stats.referrals.map((r, i) => (
                <div key={i} className={`p-3 ${theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-100'} flex justify-between items-center`}>
                  <div>
                    <div className="font-mono text-sm">{r.email}</div>
                    <div className="text-xs text-gray-400">{new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 text-xs ${
                      r.status === "qualified" ? "bg-green-500/20 text-green-500" : "bg-orange-500/20 text-orange-500"
                    }`}>
                      {r.status === "qualified" ? `+$${r.commission_amount}` : "Pending 1st Purchase"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {stats?.referrals?.length === 0 && (
          <div className={`mt-6 p-6 ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border text-center text-gray-500`}>
            <p>No referrals yet. Share your referral link to start earning!</p>
          </div>
        )}
      </div>
    </div>
  );
}


// ==================== ADMIN PANEL ====================
function AdminPanel({ theme, onLogout }) {
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [trialUsers, setTrialUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("users");
  const [loading, setLoading] = useState(false);
  const [searchEmail, setSearchEmail] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchPayments();
    fetchStats();
    fetchTrialUsers();
  }, []);

  // Update trial users every minute
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === "trials") {
        fetchTrialUsers();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [activeTab]);

  const fetchTrialUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/trial-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrialUsers(res.data.trial_users);
    } catch (error) {
      console.error("Failed to fetch trial users");
    }
  };

  const extendTrial = async (userId, days) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/users/${userId}/extend-trial?days=${days}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Trial extended by ${days} day(s)`);
      fetchTrialUsers();
      fetchUsers(searchEmail);
    } catch (error) {
      toast.error("Failed to extend trial");
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error("Failed to fetch stats");
    }
  };

  const fetchUsers = async (search = "") => {
    try {
      const token = localStorage.getItem("token");
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const res = await axios.get(`${API}/admin/users${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users);
    } catch (error) {
      toast.error("Failed to fetch users");
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/users/${userId}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserDetails(res.data);
      setSelectedUser(userId);
    } catch (error) {
      toast.error("Failed to fetch user details");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(searchEmail);
  };

  const clearSearch = () => {
    setSearchEmail("");
    fetchUsers();
  };

  const fetchPayments = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(res.data.payments);
    } catch (error) {
      console.error("Failed to fetch payments");
    }
  };

  const toggleBan = async (userId, currentBanned) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/users/ban`, 
        { user_id: userId, banned: !currentBanned },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(currentBanned ? "User unbanned" : "User banned");
      fetchUsers(searchEmail);
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const unlockUser = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/users/unlock?user_id=${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Account unlocked");
      fetchUsers(searchEmail);
    } catch (error) {
      toast.error("Failed to unlock account");
    }
  };

  const resetDevices = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/users/${userId}/reset-devices`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Devices reset");
      if (selectedUser === userId) fetchUserDetails(userId);
    } catch (error) {
      toast.error("Failed to reset devices");
    }
  };

  const resetIPs = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/users/${userId}/reset-ips`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("IPs reset");
      if (selectedUser === userId) fetchUserDetails(userId);
    } catch (error) {
      toast.error("Failed to reset IPs");
    }
  };

  const forceLogout = async (userId, sessionId = null) => {
    try {
      const token = localStorage.getItem("token");
      const params = sessionId ? `?session_id=${sessionId}` : "";
      await axios.post(`${API}/admin/users/${userId}/force-logout${params}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(sessionId ? "Session terminated" : "All sessions terminated");
      if (selectedUser === userId) fetchUserDetails(userId);
      fetchUsers(searchEmail);
    } catch (error) {
      toast.error("Failed to logout user");
    }
  };

  const addSubscription = async (userId, type) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/users/subscription`,
        { user_id: userId, subscription_type: type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Subscription added");
      fetchUsers(searchEmail);
    } catch (error) {
      toast.error("Failed to add subscription");
    }
  };

  const adjustDeviceLimit = async (userId, change) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/admin/users/${userId}/adjust-device-limit?change=${change}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Device limit: ${res.data.device_limit}`);
      if (selectedUser === userId) fetchUserDetails(userId);
    } catch (error) {
      toast.error("Failed to adjust device limit");
    }
  };

  const adjustIpLimit = async (userId, change) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/admin/users/${userId}/adjust-ip-limit?change=${change}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`IP limit: ${res.data.ip_limit}`);
      if (selectedUser === userId) fetchUserDetails(userId);
    } catch (error) {
      toast.error("Failed to adjust IP limit");
    }
  };

  const isAccountLocked = (user) => {
    if (!user.locked_until) return false;
    return new Date(user.locked_until) > new Date();
  };

  const removeSubscription = async (userId) => {
    if (!window.confirm("Are you sure you want to remove this user's subscription? They will need to purchase a new subscription.")) {
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/users/revoke-subscription?user_id=${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Subscription removed");
      fetchUsers(searchEmail);
      if (selectedUser === userId) fetchUserDetails(userId);
    } catch (error) {
      toast.error("Failed to remove subscription");
    }
  };

  const deleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to PERMANENTLY DELETE user ${userEmail}? This action cannot be undone!`)) {
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User ${userEmail} deleted`);
      setSelectedUser(null);
      setUserDetails(null);
      fetchUsers(searchEmail);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const assignSubUserSlots = async (userId, slots) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/users/${userId}/assign-sub-user-slots`, {
        user_id: userId,
        max_sub_users: slots
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Assigned ${slots} sub-user slots`);
      fetchUserDetails(userId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign sub-user slots");
    }
  };

  const viewSubUsers = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/users/${userId}/sub-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data;
      
      // Show sub-users in an alert or modal
      let message = `Main User: ${data.main_user.email}\nSub-Users (${data.main_user.current_sub_users}/${data.main_user.max_sub_users}):\n\n`;
      if (data.sub_users.length === 0) {
        message += "No sub-users created yet.";
      } else {
        data.sub_users.forEach((sub, i) => {
          message += `${i+1}. ${sub.username} (${sub.email}) - ${sub.is_active ? 'Active' : 'Disabled'}\n`;
        });
      }
      alert(message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch sub-users");
    }
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-[#09090b] text-white' : 'bg-gray-100'}`}>
      {/* Header */}
      <header className={`border-b ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'} p-4`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#ccff00]" />
            <span className="font-bold text-xl">Admin Panel</span>
          </div>
          <Button onClick={onLogout} variant="outline" className={theme === 'dark' ? 'border-[#3f3f46]' : ''}>
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className={`p-4 ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border`}>
              <div className="text-2xl font-bold text-[#ccff00]">{stats.total_users}</div>
              <div className="text-sm text-gray-400">Total Users</div>
            </div>
            <div className={`p-4 ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border`}>
              <div className="text-2xl font-bold text-green-500">{stats.active_subscriptions}</div>
              <div className="text-sm text-gray-400">Active Subs</div>
            </div>
            <div className={`p-4 ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border`}>
              <div className="text-2xl font-bold text-blue-500">{stats.users_with_active_sessions}</div>
              <div className="text-sm text-gray-400">Active Sessions</div>
            </div>
            <div className={`p-4 ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border`}>
              <div className="text-2xl font-bold text-orange-500">{stats.referral_stats?.pending_withdrawals || 0}</div>
              <div className="text-sm text-gray-400">Pending Withdrawals</div>
            </div>
            <div className={`p-4 ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border`}>
              <div className="text-2xl font-bold text-green-400">${stats.referral_stats?.total_paid_out?.toFixed(2) || "0.00"}</div>
              <div className="text-sm text-gray-400">Total Paid Out</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab("users")}
            className={`px-4 py-2 font-bold ${activeTab === "users" ? "bg-[#ccff00] text-black" : theme === 'dark' ? "bg-[#18181b]" : "bg-white"}`}
          >
            <Users className="w-4 h-4 inline mr-2" /> Users
          </button>
          <button
            onClick={() => setActiveTab("withdrawals")}
            className={`px-4 py-2 font-bold ${activeTab === "withdrawals" ? "bg-[#ccff00] text-black" : theme === 'dark' ? "bg-[#18181b]" : "bg-white"}`}
          >
            <Wallet className="w-4 h-4 inline mr-2" /> Withdrawals
            {stats?.referral_stats?.pending_withdrawals > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                {stats.referral_stats.pending_withdrawals}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("referrals")}
            className={`px-4 py-2 font-bold ${activeTab === "referrals" ? "bg-[#ccff00] text-black" : theme === 'dark' ? "bg-[#18181b]" : "bg-white"}`}
          >
            <Gift className="w-4 h-4 inline mr-2" /> Referrals
          </button>
          <button
            onClick={() => setActiveTab("payments")}
            className={`px-4 py-2 font-bold ${activeTab === "payments" ? "bg-[#ccff00] text-black" : theme === 'dark' ? "bg-[#18181b]" : "bg-white"}`}
          >
            <DollarSign className="w-4 h-4 inline mr-2" /> Payments
          </button>
        </div>

        {/* User Details Modal */}
        {selectedUser && userDetails && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`w-full max-w-2xl max-h-[80vh] overflow-y-auto ${theme === 'dark' ? 'bg-[#18181b]' : 'bg-white'} p-6`}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">User Details: {userDetails.user.email}</h2>
                <Button variant="ghost" onClick={() => { setSelectedUser(null); setUserDetails(null); }}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                <div className={`text-center p-2 ${theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-100'}`}>
                  <div className="text-lg font-bold">{userDetails.stats.active_sessions}</div>
                  <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Sessions</div>
                </div>
                <div className={`text-center p-2 ${theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-100'}`}>
                  <div className="text-lg font-bold">{userDetails.stats.registered_devices}</div>
                  <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Devices</div>
                </div>
                <div className={`text-center p-2 ${theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-100'}`}>
                  <div className="text-lg font-bold">{userDetails.stats.allowed_ips}</div>
                  <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>IPs</div>
                </div>
                <div className={`text-center p-2 ${theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-100'}`}>
                  <div className="text-lg font-bold">{userDetails.limits.users}</div>
                  <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Plan Users</div>
                </div>
              </div>

              {/* Limit Controls */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div className={`p-4 ${theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-100'}`}>
                  <div className="text-sm font-bold mb-2">Device Limit</div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-400">{userDetails.stats.registered_devices} / {userDetails.limits.devices} used</div>
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => adjustDeviceLimit(selectedUser, -1)}
                        disabled={userDetails.limits.devices <= 1}
                        className="w-10 h-10 bg-red-500 hover:bg-red-600 disabled:bg-gray-600 text-white font-bold text-xl flex items-center justify-center"
                      >
                        -
                      </button>
                      <span className="text-2xl font-bold text-[#ccff00] w-12 text-center">{userDetails.limits.devices}</span>
                      <button 
                        onClick={() => adjustDeviceLimit(selectedUser, 1)}
                        className="w-10 h-10 bg-green-500 hover:bg-green-600 text-white font-bold text-xl flex items-center justify-center"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
                <div className={`p-4 ${theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-100'}`}>
                  <div className="text-sm font-bold mb-2">IP Limit</div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-400">{userDetails.stats.allowed_ips} / {userDetails.limits.ips} used</div>
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => adjustIpLimit(selectedUser, -1)}
                        disabled={userDetails.limits.ips <= 1}
                        className="w-10 h-10 bg-red-500 hover:bg-red-600 disabled:bg-gray-600 text-white font-bold text-xl flex items-center justify-center"
                      >
                        -
                      </button>
                      <span className="text-2xl font-bold text-[#ccff00] w-12 text-center">{userDetails.limits.ips}</span>
                      <button 
                        onClick={() => adjustIpLimit(selectedUser, 1)}
                        className="w-10 h-10 bg-green-500 hover:bg-green-600 text-white font-bold text-xl flex items-center justify-center"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Active Sessions */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold">Active Sessions</h3>
                  <Button size="sm" variant="outline" onClick={() => forceLogout(selectedUser)} className="text-red-500">
                    Logout All
                  </Button>
                </div>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {userDetails.user.active_sessions?.map((session, i) => (
                    <div key={i} className="flex justify-between items-center p-2 bg-[#27272a] text-sm">
                      <div>
                        <div>{session.device_name}</div>
                        <div className="text-xs text-gray-400">IP: {session.ip_address}</div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => forceLogout(selectedUser, session.session_id)} className="text-red-400">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {(!userDetails.user.active_sessions || userDetails.user.active_sessions.length === 0) && (
                    <div className="text-gray-500 text-sm">No active sessions</div>
                  )}
                </div>
              </div>

              {/* Allowed IPs */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold">Allowed IPs ({userDetails.user.allowed_ips?.length || 0}/{userDetails.limits.ips})</h3>
                  <Button size="sm" variant="outline" onClick={() => resetIPs(selectedUser)} className="text-orange-500">
                    Reset All
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {userDetails.user.allowed_ips?.map((ip, i) => (
                    <span key={i} className="px-2 py-1 bg-[#27272a] text-sm">{ip}</span>
                  ))}
                  {(!userDetails.user.allowed_ips || userDetails.user.allowed_ips.length === 0) && (
                    <div className="text-gray-500 text-sm">No IPs registered</div>
                  )}
                </div>
              </div>

              {/* Registered Devices */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold">Registered Devices ({userDetails.user.registered_devices?.length || 0}/{userDetails.limits.devices})</h3>
                  <Button size="sm" variant="outline" onClick={() => resetDevices(selectedUser)} className="text-orange-500">
                    Reset All
                  </Button>
                </div>
                <div className="space-y-1">
                  {userDetails.user.registered_devices?.map((device, i) => (
                    <div key={i} className="p-2 bg-[#27272a] text-sm">
                      <div>{device.name}</div>
                      <div className="text-xs text-gray-400">Last used: {new Date(device.last_used).toLocaleString()}</div>
                    </div>
                  ))}
                  {(!userDetails.user.registered_devices || userDetails.user.registered_devices.length === 0) && (
                    <div className="text-gray-500 text-sm">No devices registered</div>
                  )}
                </div>
              </div>

              {/* Login History */}
              <div>
                <h3 className="font-bold mb-2">Recent Login History</h3>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {userDetails.user.login_history?.slice(-10).reverse().map((entry, i) => (
                    <div key={i} className={`flex flex-col sm:flex-row sm:justify-between p-2 ${theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-100'} text-sm`}>
                      <div>
                        <span className={entry.action === 'login' ? 'text-green-400' : 'text-gray-400'}>{entry.action}</span>
                        <span className={`ml-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{entry.device}</span>
                      </div>
                      <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {entry.ip} - {new Date(entry.timestamp).toLocaleString()}
                      </div>
                    </div>
                  ))}
                  {(!userDetails.user.login_history || userDetails.user.login_history.length === 0) && (
                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>No login history</div>
                  )}
                </div>
              </div>

              {/* Sub-User Management Section */}
              <div className="mt-4 pt-4 border-t border-blue-500/30">
                <h3 className="font-bold text-blue-400 mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5" /> Sub-User Management
                </h3>
                <div className="flex items-center gap-4 mb-3">
                  <div className="flex-1">
                    <div className="text-sm text-gray-400 mb-1">Max Sub-Users Allowed</div>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => assignSubUserSlots(selectedUser, Math.max(0, (userDetails.user.max_sub_users || 0) - 1))}
                        className="w-10 h-10 bg-red-500 hover:bg-red-600 text-white font-bold text-xl flex items-center justify-center"
                      >
                        -
                      </button>
                      <span className="text-2xl font-bold text-blue-400 w-12 text-center">{userDetails.user.max_sub_users || 0}</span>
                      <button 
                        onClick={() => assignSubUserSlots(selectedUser, (userDetails.user.max_sub_users || 0) + 1)}
                        className="w-10 h-10 bg-green-500 hover:bg-green-600 text-white font-bold text-xl flex items-center justify-center"
                      >
                        +
                      </button>
                      <button 
                        onClick={() => assignSubUserSlots(selectedUser, 10)}
                        className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-bold"
                      >
                        Set 10
                      </button>
                    </div>
                  </div>
                </div>
                {(userDetails.user.max_sub_users || 0) > 0 && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => viewSubUsers(selectedUser)}
                    className="text-blue-400 border-blue-400"
                  >
                    View Sub-Users
                  </Button>
                )}
              </div>

              {/* Danger Zone Actions */}
              <div className="mt-6 pt-4 border-t border-red-500/30">
                <h3 className="font-bold text-red-500 mb-3">Danger Zone</h3>
                <div className="flex flex-wrap gap-2">
                  {userDetails.user.subscription_status === "active" && (
                    <Button
                      onClick={() => removeSubscription(selectedUser)}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      Remove Subscription
                    </Button>
                  )}
                  <Button
                    onClick={() => deleteUser(selectedUser, userDetails.user.email)}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete User Permanently
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Removing subscription will require user to purchase again. Deleting user is permanent.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className={`${theme === 'dark' ? 'bg-[#18181b]' : 'bg-white'} border ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'}`}>
            {/* Search Bar */}
            <div className="p-4 border-b border-[#3f3f46]">
              <form onSubmit={handleSearch} className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Search by email..."
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  className={`flex-1 ${theme === 'dark' ? 'bg-[#27272a] border-[#3f3f46] text-white' : ''}`}
                />
                <Button type="submit" className="bg-[#ccff00] text-black">
                  <Search className="w-4 h-4 mr-2" /> Search
                </Button>
                {searchEmail && (
                  <Button type="button" variant="outline" onClick={clearSearch} className={theme === 'dark' ? 'border-[#3f3f46]' : ''}>
                    Clear
                  </Button>
                )}
              </form>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-50'}>
                  <tr>
                    <th className="p-4 text-left text-sm font-bold uppercase">User</th>
                    <th className="p-4 text-left text-sm font-bold uppercase">Status</th>
                    <th className="p-4 text-left text-sm font-bold uppercase">Subscription</th>
                    <th className="p-4 text-left text-sm font-bold uppercase">Sessions</th>
                    <th className="p-4 text-left text-sm font-bold uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className={`border-t ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'}`}>
                      <td className="p-4">
                        <div className="font-bold">{user.name}</div>
                        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</div>
                      </td>
                      <td className="p-4">
                        {user.is_banned ? (
                          <span className="px-2 py-1 bg-red-500/20 text-red-500 text-sm">BANNED</span>
                        ) : user.is_admin ? (
                          <span className="px-2 py-1 bg-[#ccff00]/20 text-[#ccff00] text-sm">ADMIN</span>
                        ) : isAccountLocked(user) ? (
                          <span className="px-2 py-1 bg-orange-500/20 text-orange-500 text-sm">LOCKED</span>
                        ) : (
                          <span className="px-2 py-1 bg-green-500/20 text-green-500 text-sm">ACTIVE</span>
                        )}
                      </td>
                      <td className="p-4">
                        {user.subscription_status === "active" ? (
                          <div>
                            <span className="text-[#ccff00]">{user.subscription_type?.toUpperCase()}</span>
                            {user.max_users > 1 && (
                              <span className="text-xs text-gray-400 block">{user.max_users} users</span>
                            )}
                          </div>
                        ) : (
                          <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}>None</span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="text-sm">
                          <span className="text-blue-400">{user.active_sessions?.length || 0}</span>
                          <span className="text-gray-500"> / </span>
                          <span className="text-gray-400">{user.registered_devices?.length || 0}D</span>
                          <span className="text-gray-500"> / </span>
                          <span className="text-gray-400">{user.allowed_ips?.length || 0}IP</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => fetchUserDetails(user.id)}
                            className="text-blue-400"
                          >
                            Details
                          </Button>
                          {!user.is_admin && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => toggleBan(user.id, user.is_banned)}
                                className={user.is_banned ? "text-green-500" : "text-red-500"}
                              >
                                {user.is_banned ? "Unban" : "Ban"}
                              </Button>
                              {isAccountLocked(user) && (
                                <Button
                                  size="sm"
                                  onClick={() => unlockUser(user.id)}
                                  className="bg-orange-500 text-white"
                                >
                                  Unlock
                                </Button>
                              )}
                              <Button
                                size="sm"
                                onClick={() => addSubscription(user.id, "monthly")}
                                className="bg-blue-500 text-white"
                              >
                                +Monthly
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => addSubscription(user.id, "lifetime")}
                                className="bg-[#ccff00] text-black"
                              >
                                +Lifetime
                              </Button>
                              {user.subscription_status === "active" && (
                                <Button
                                  size="sm"
                                  onClick={() => removeSubscription(user.id)}
                                  className="bg-orange-600 text-white"
                                >
                                  -Sub
                                </Button>
                              )}
                              <Button
                                size="sm"
                                onClick={() => deleteUser(user.id, user.email)}
                                className="bg-red-600 text-white"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === "payments" && (
          <div className={`${theme === 'dark' ? 'bg-[#18181b]' : 'bg-white'} border ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'}`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-50'}>
                  <tr>
                    <th className="p-4 text-left text-sm font-bold uppercase">Order ID</th>
                    <th className="p-4 text-left text-sm font-bold uppercase">Plan</th>
                    <th className="p-4 text-left text-sm font-bold uppercase">Amount</th>
                    <th className="p-4 text-left text-sm font-bold uppercase">Status</th>
                    <th className="p-4 text-left text-sm font-bold uppercase">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id} className={`border-t ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'}`}>
                      <td className="p-4 font-mono text-sm">{payment.order_id}</td>
                      <td className="p-4">{payment.plan_type?.toUpperCase()}</td>
                      <td className="p-4">
                        ${payment.price_amount} / {payment.pay_amount} {payment.pay_currency?.toUpperCase()}
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-sm ${
                          payment.status === "finished" ? "bg-green-500/20 text-green-500" :
                          payment.status === "waiting" ? "bg-yellow-500/20 text-yellow-500" :
                          "bg-red-500/20 text-red-500"
                        }`}>
                          {payment.status?.toUpperCase()}
                        </span>
                      </td>
                      <td className="p-4 text-sm">{new Date(payment.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Trial Users Tab */}
        {activeTab === "trials" && (
          <div className={`${theme === 'dark' ? 'bg-[#18181b]' : 'bg-white'} border ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'}`}>
            <div className="p-4 border-b flex justify-between items-center">
              <div>
                <h3 className="font-bold">Trial Users</h3>
                <p className="text-sm text-gray-500">Manage trial subscriptions</p>
              </div>
              <Button onClick={fetchTrialUsers} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-50'}>
                  <tr>
                    <th className="p-4 text-left text-sm font-bold uppercase">User</th>
                    <th className="p-4 text-left text-sm font-bold uppercase">Plan</th>
                    <th className="p-4 text-left text-sm font-bold uppercase">Time Left</th>
                    <th className="p-4 text-left text-sm font-bold uppercase">Status</th>
                    <th className="p-4 text-left text-sm font-bold uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {trialUsers.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="p-8 text-center text-gray-500">
                        No trial users found
                      </td>
                    </tr>
                  ) : (
                    trialUsers.map((user) => (
                      <tr key={user.id} className={`border-t ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'}`}>
                        <td className="p-4">
                          <div className="font-medium">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </td>
                        <td className="p-4">
                          <span className="px-2 py-1 bg-orange-500/20 text-orange-500 text-sm">
                            {user.plan?.toUpperCase() || "TRIAL"}
                          </span>
                        </td>
                        <td className="p-4">
                          {user.is_expired ? (
                            <span className="text-red-500 font-bold">EXPIRED</span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Clock className={`w-4 h-4 ${user.hours_left < 6 ? 'text-red-500' : user.hours_left < 24 ? 'text-orange-500' : 'text-green-500'}`} />
                              <span className={`font-bold ${user.hours_left < 6 ? 'text-red-500' : user.hours_left < 24 ? 'text-orange-500' : 'text-green-500'}`}>
                                {user.hours_left}h {user.minutes_left}m
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-sm ${user.is_expired ? 'bg-red-500/20 text-red-500' : 'bg-green-500/20 text-green-500'}`}>
                            {user.is_expired ? "EXPIRED" : "ACTIVE"}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2 flex-wrap">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => extendTrial(user.id, 1)}
                              className="text-xs"
                            >
                              +1 Day
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => extendTrial(user.id, 3)}
                              className="text-xs"
                            >
                              +3 Days
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => extendTrial(user.id, 7)}
                              className="text-xs"
                            >
                              +7 Days
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Withdrawals Tab */}
        {activeTab === "withdrawals" && (
          <WithdrawalsTab theme={theme} />
        )}

        {/* Referrals Tab */}
        {activeTab === "referrals" && (
          <ReferralsTab theme={theme} />
        )}
      </div>
    </div>
  );
}

// Admin Withdrawals Tab Component
function WithdrawalsTab({ theme }) {
  const [withdrawals, setWithdrawals] = useState([]);
  const [filter, setFilter] = useState("pending");

  useEffect(() => {
    fetchWithdrawals();
  }, [filter]);

  const fetchWithdrawals = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = filter !== "all" ? `?status=${filter}` : "";
      const res = await axios.get(`${API}/admin/withdrawals${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWithdrawals(res.data.withdrawals);
    } catch (error) {
      toast.error("Failed to fetch withdrawals");
    }
  };

  const processWithdrawal = async (withdrawalId, action) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/withdrawals/action`, {
        withdrawal_id: withdrawalId,
        action: action
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Withdrawal ${action}d`);
      fetchWithdrawals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    }
  };

  return (
    <div className={`${theme === 'dark' ? 'bg-[#18181b]' : 'bg-white'} border ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'}`}>
      <div className="p-4 border-b border-[#3f3f46] flex gap-2">
        {["pending", "approved", "rejected", "all"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 text-sm ${filter === f ? "bg-[#ccff00] text-black" : theme === 'dark' ? "bg-[#27272a]" : "bg-gray-100"}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-50'}>
            <tr>
              <th className="p-4 text-left text-sm font-bold uppercase">User</th>
              <th className="p-4 text-left text-sm font-bold uppercase">Amount</th>
              <th className="p-4 text-left text-sm font-bold uppercase">Method</th>
              <th className="p-4 text-left text-sm font-bold uppercase">Address</th>
              <th className="p-4 text-left text-sm font-bold uppercase">Status</th>
              <th className="p-4 text-left text-sm font-bold uppercase">Date</th>
              <th className="p-4 text-left text-sm font-bold uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {withdrawals.map((w) => (
              <tr key={w.id} className={`border-t ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'}`}>
                <td className="p-4 text-sm">{w.user_email}</td>
                <td className="p-4 font-bold text-[#ccff00]">${w.amount.toFixed(2)}</td>
                <td className="p-4">
                  <span className={`px-2 py-1 text-xs ${w.method === "usdt" ? "bg-blue-500/20 text-blue-400" : "bg-purple-500/20 text-purple-400"}`}>
                    {w.method.toUpperCase()}
                  </span>
                </td>
                <td className="p-4 font-mono text-xs max-w-[200px] truncate">
                  {w.method === "usdt" ? w.wallet_address : w.upi_address}
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 text-xs ${
                    w.status === "approved" ? "bg-green-500/20 text-green-500" :
                    w.status === "rejected" ? "bg-red-500/20 text-red-500" :
                    "bg-orange-500/20 text-orange-500"
                  }`}>
                    {w.status.toUpperCase()}
                  </span>
                </td>
                <td className="p-4 text-sm">{new Date(w.created_at).toLocaleDateString()}</td>
                <td className="p-4">
                  {w.status === "pending" && (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => processWithdrawal(w.id, "approve")} className="bg-green-500 text-white">
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="sm" onClick={() => processWithdrawal(w.id, "reject")} className="bg-red-500 text-white">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {withdrawals.length === 0 && (
          <p className="text-center text-gray-500 py-8">No withdrawals found</p>
        )}
      </div>
    </div>
  );
}

// Admin Referrals Tab Component
function ReferralsTab({ theme }) {
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/referral-leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(res.data.leaderboard);
    } catch (error) {
      console.error("Failed to fetch leaderboard");
    }
  };

  const addCommission = async (userId) => {
    const amount = prompt("Enter commission amount to add ($):");
    if (!amount || isNaN(parseFloat(amount))) return;

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/users/${userId}/add-commission?amount=${parseFloat(amount)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`$${amount} commission added`);
      fetchLeaderboard();
    } catch (error) {
      toast.error("Failed to add commission");
    }
  };

  return (
    <div className={`${theme === 'dark' ? 'bg-[#18181b]' : 'bg-white'} border ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'}`}>
      <div className="p-4 border-b border-[#3f3f46]">
        <h3 className="font-bold">Referral Leaderboard</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-50'}>
            <tr>
              <th className="p-4 text-left text-sm font-bold uppercase">#</th>
              <th className="p-4 text-left text-sm font-bold uppercase">User</th>
              <th className="p-4 text-left text-sm font-bold uppercase">Code</th>
              <th className="p-4 text-left text-sm font-bold uppercase">Total Refs</th>
              <th className="p-4 text-left text-sm font-bold uppercase">Paid Refs</th>
              <th className="p-4 text-left text-sm font-bold uppercase">Balance</th>
              <th className="p-4 text-left text-sm font-bold uppercase">Total Earned</th>
              <th className="p-4 text-left text-sm font-bold uppercase">Withdrawn</th>
              <th className="p-4 text-left text-sm font-bold uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leaderboard.map((user, i) => (
              <tr key={user.id} className={`border-t ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'}`}>
                <td className="p-4 font-bold text-[#ccff00]">#{i + 1}</td>
                <td className="p-4">
                  <div className="font-bold">{user.name}</div>
                  <div className="text-xs text-gray-400">{user.email}</div>
                </td>
                <td className="p-4 font-mono text-sm">{user.referral_code}</td>
                <td className="p-4">{user.referral_count || 0}</td>
                <td className="p-4 text-green-500">{user.successful_referrals || 0}</td>
                <td className="p-4 text-[#ccff00]">${(user.commission_balance || 0).toFixed(2)}</td>
                <td className="p-4 text-blue-400">${(user.total_commission_earned || 0).toFixed(2)}</td>
                <td className="p-4">${(user.total_withdrawn || 0).toFixed(2)}</td>
                <td className="p-4">
                  <Button size="sm" onClick={() => addCommission(user.id)} className="bg-[#ccff00] text-black">
                    +$
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {leaderboard.length === 0 && (
          <p className="text-center text-gray-500 py-8">No referrers yet</p>
        )}
      </div>
    </div>
  );
}


// ==================== MAIN GENERATOR PAGE ====================
function GeneratorPage({ user, credits, refreshCredits, theme, toggleTheme, onLogout, onReferral, onUpgrade, onProfile, onAIBlog, onSubUsers }) {
  const [keywords, setKeywords] = useState(INITIAL_KEYWORDS);
  const [phoneNumbers, setPhoneNumbers] = useState([""]); // Array of up to 3 phone numbers
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("en");
  const [pages, setPages] = useState(1);
  const [generatedContent, setGeneratedContent] = useState("");
  const [wordCount, setWordCount] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [includeFaq, setIncludeFaq] = useState(false);
  const [faqMode, setFaqMode] = useState("auto"); // "auto" or "custom"
  const [customFaqs, setCustomFaqs] = useState(INITIAL_FAQS);
  const [subscriptionTimeLeft, setSubscriptionTimeLeft] = useState(null);
  const [category, setCategory] = useState("airlines"); // airlines, crypto, car_rental
  const [phoneRegion, setPhoneRegion] = useState("mx"); // mx, us, other
  const [pdfQuality, setPdfQuality] = useState("hd"); // standard or hd
  const [useFancyNumbers, setUseFancyNumbers] = useState(true); // fancy unicode numbers
  const [useEmojis, setUseEmojis] = useState(true); // include emojis in content
  const [emojiDensity, setEmojiDensity] = useState(100); // 30, 50, 70, 100 percent
  
  // Bulk PDF generation states
  const [bulkCount, setBulkCount] = useState(1); // 1-20 PDFs
  const [isBulkGenerating, setIsBulkGenerating] = useState(false);
  
  // PDF Customization states
  const [headerColor, setHeaderColor] = useState("blue"); // blue, green, red, purple, orange
  const [logoUrl, setLogoUrl] = useState(""); // Optional logo URL
  const [pdfPreview, setPdfPreview] = useState(null); // Base64 PDF for preview
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  
  // Meta description states
  const [metaMode, setMetaMode] = useState("auto"); // "auto" or "manual"
  const [metaDescription, setMetaDescription] = useState("");
  
  // Keyword mode: tracks if keywords were auto-fetched or manually entered
  const [useAutoKeywords, setUseAutoKeywords] = useState(false); // false = manual only, true = auto-filled
  
  // Content seed for PDF consistency - stores seed from last generation
  const [contentSeed, setContentSeed] = useState("");
  
  // AI Blogging states
  const [showBlogging, setShowBlogging] = useState(false);
  const [blogTopic, setBlogTopic] = useState("");
  const [blogKeywords, setBlogKeywords] = useState("");
  const [blogLinks, setBlogLinks] = useState([{ text: "", url: "" }]);
  const [blogContent, setBlogContent] = useState("");
  const [isGeneratingBlog, setIsGeneratingBlog] = useState(false);
  
  // Phone number helpers
  const addPhoneNumber = () => {
    if (phoneNumbers.length < 3) {
      setPhoneNumbers([...phoneNumbers, ""]);
    }
  };
  
  const removePhoneNumber = (index) => {
    if (phoneNumbers.length > 1) {
      setPhoneNumbers(phoneNumbers.filter((_, i) => i !== index));
    }
  };
  
  const updatePhoneNumber = (index, value) => {
    const newNumbers = [...phoneNumbers];
    newNumbers[index] = value;
    setPhoneNumbers(newNumbers);
  };
  
  const getValidPhoneNumbers = () => phoneNumbers.filter(p => p.trim()).join(",");
  
  // Blog link helpers
  const addBlogLink = () => {
    if (blogLinks.length < 5) {
      setBlogLinks([...blogLinks, { text: "", url: "" }]);
    }
  };
  
  const removeBlogLink = (index) => {
    if (blogLinks.length > 1) {
      setBlogLinks(blogLinks.filter((_, i) => i !== index));
    }
  };
  
  const updateBlogLink = (index, field, value) => {
    const newLinks = [...blogLinks];
    newLinks[index][field] = value;
    setBlogLinks(newLinks);
  };
  
  // FREE ACCESS MODE - All users are premium until September 1, 2026
  const subscriptionType = user?.subscription_type?.toLowerCase() || "";
  const isPremiumUser = true; // FREE ACCESS - Everyone is premium

  // Subscription countdown timer (trial = hours, monthly = days)
  useEffect(() => {
    if ((user?.subscription_type === "trial" || user?.subscription_type === "monthly") && user?.subscription_expires) {
      const updateCountdown = () => {
        const now = new Date();
        const expires = new Date(user.subscription_expires);
        const diff = expires - now;
        
        if (diff <= 0) {
          setSubscriptionTimeLeft({ expired: true, days: 0, hours: 0, minutes: 0, seconds: 0, type: user.subscription_type });
        } else {
          const days = Math.floor(diff / (1000 * 60 * 60 * 24));
          const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);
          setSubscriptionTimeLeft({ expired: false, days, hours, minutes, seconds, type: user.subscription_type });
        }
      };
      
      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    } else {
      setSubscriptionTimeLeft(null);
    }
  }, [user?.subscription_type, user?.subscription_expires]);

  const getKeywordsString = useCallback(() => {
    return keywords.filter(k => k.trim()).join(", ");
  }, [keywords]);

  const updateKeyword = (index, value) => {
    const newKeywords = [...keywords];
    newKeywords[index] = value;
    setKeywords(newKeywords);
    setUseAutoKeywords(false); // User is manually editing, so use only manual keywords
  };

  const autoFetchKeywords = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/suggest-keywords`, null, {
        params: { title: title || "", topic: keywords[0] || "", category: category, language: language },
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.keywords && res.data.keywords.length > 0) {
        setKeywords(res.data.keywords);
        setUseAutoKeywords(true); // Mark as auto-filled to include category keywords
        toast.success(`10 ${category.replace('_', ' ')} keywords auto-filled!`);
      }
    } catch (error) {
      toast.error("Failed to fetch keywords");
    }
  };

  const updateFaq = (index, field, value) => {
    const newFaqs = [...customFaqs];
    newFaqs[index][field] = value;
    setCustomFaqs(newFaqs);
  };

  const generateContent = useCallback(async () => {
    const keywordsString = getKeywordsString();
    const validPhones = getValidPhoneNumbers();
    if (!keywordsString || !validPhones) {
      toast.error("AT LEAST ONE KEYWORD AND PHONE NUMBER REQUIRED");
      return;
    }

    setIsGenerating(true);
    try {
      // Preview generation - no credit cost, no auth required
      const response = await axios.post(`${API}/preview-content`, {
        keywords: keywordsString,
        phone_number: validPhones,
        language,
        pages,
        title: title.trim(),
        include_faq: includeFaq,
        custom_faqs: faqMode === "custom" ? customFaqs.filter(f => f.question && f.answer) : null,
        category: category,
        phone_region: phoneRegion,
        use_fancy_numbers: useFancyNumbers,
        use_emojis: useEmojis,
        emoji_density: emojiDensity,
        meta_mode: metaMode,
        meta_description: metaMode === "manual" ? metaDescription : "",
        use_auto_keywords: useAutoKeywords,
      });

      setGeneratedContent(response.data.content);
      setWordCount(response.data.word_count);
      setContentSeed(response.data.content_seed || ""); // Store seed for PDF
      toast.success(`PREVIEW: ${response.data.word_count} WORDS`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "GENERATION FAILED");
    } finally {
      setIsGenerating(false);
    }
  }, [keywords, phoneNumbers, language, pages, title, includeFaq, faqMode, customFaqs, getKeywordsString, category, phoneRegion, useFancyNumbers, useEmojis, emojiDensity, metaMode, metaDescription, getValidPhoneNumbers, useAutoKeywords]);

  const downloadPDF = async () => {
    const keywordsString = getKeywordsString();
    const validPhones = getValidPhoneNumbers();
    if (!keywordsString || !validPhones) {
      toast.error("AT LEAST ONE KEYWORD AND PHONE NUMBER REQUIRED");
      return;
    }

    // Check if user has enough credits
    if (credits < 1) {
      toast.error("INSUFFICIENT CREDITS - Get more at QuickIndex.Pro");
      return;
    }

    setIsDownloading(true);
    try {
      const activationCode = localStorage.getItem("activation_code");
      const response = await axios.post(
        `${API}/quickindex/generate-bulk-pdf`,
        {
          activation_code: activationCode,
          count: 1,
          keywords: keywordsString,
          phone_number: validPhones,
          language,
          category: category,
          phone_region: phoneRegion,
          use_fancy_numbers: useFancyNumbers,
          use_emojis: useEmojis,
          emoji_density: emojiDensity,
          include_faq: includeFaq,
          base_title: title.trim() || keywordsString.split(",")[0].trim(),
          hd_quality: true,
          header_color: headerColor,
          logo_url: logoUrl,
        },
        { 
          responseType: "blob",
          timeout: 120000
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const keywordSlug = keywordsString.split(",")[0].trim().replace(/\s+/g, "_").slice(0, 20);
      link.setAttribute("download", `seo_content_${keywordSlug}_HD.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("HD PDF DOWNLOADED - 1 credit used");
      // Refresh credits after download
      if (refreshCredits) refreshCredits();
    } catch (error) {
      console.error("PDF download error:", error);
      if (error.response?.status === 402) {
        toast.error("INSUFFICIENT CREDITS");
      } else if (error.response?.status === 401) {
        toast.error("INVALID ACTIVATION CODE - Please re-login");
      } else {
        toast.error("DOWNLOAD FAILED");
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // Bulk PDF Generation (1-20 PDFs)
  const downloadBulkPDFs = async () => {
    const keywordsString = getKeywordsString();
    const validPhones = getValidPhoneNumbers();
    if (!keywordsString || !validPhones) {
      toast.error("AT LEAST ONE KEYWORD AND PHONE NUMBER REQUIRED");
      return;
    }

    // Check if user has enough credits
    if (credits < bulkCount) {
      toast.error(`INSUFFICIENT CREDITS - Need ${bulkCount}, have ${credits}`);
      return;
    }

    setIsBulkGenerating(true);
    try {
      const activationCode = localStorage.getItem("activation_code");
      toast.info(`Generating ${bulkCount} HD PDFs... This may take a moment.`);
      
      const response = await axios.post(
        `${API}/quickindex/generate-bulk-pdf`,
        {
          activation_code: activationCode,
          count: bulkCount,
          keywords: keywordsString,
          phone_number: validPhones,
          language,
          category: category,
          phone_region: phoneRegion,
          use_fancy_numbers: useFancyNumbers,
          use_emojis: useEmojis,
          emoji_density: emojiDensity,
          include_faq: includeFaq,
          base_title: title.trim() || keywordsString.split(",")[0].trim(),
          hd_quality: true,
          header_color: headerColor,
          logo_url: logoUrl,
        },
        { 
          responseType: "blob",
          timeout: 300000 // 5 minutes for bulk generation
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const keywordSlug = keywordsString.split(",")[0].trim().replace(/\s+/g, "_").slice(0, 20);
      link.setAttribute("download", `seo_content_${keywordSlug}_${bulkCount}pdfs.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${bulkCount} PDFs DOWNLOADED - ${bulkCount} credits used`);
      // Refresh credits after download
      if (refreshCredits) refreshCredits();
    } catch (error) {
      console.error("Bulk PDF error:", error);
      if (error.response?.status === 402) {
        toast.error("INSUFFICIENT CREDITS");
      } else if (error.response?.status === 401) {
        toast.error("INVALID ACTIVATION CODE - Please re-login");
      } else {
        toast.error("BULK GENERATION FAILED");
      }
    } finally {
      setIsBulkGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    if (!generatedContent) {
      toast.error("NO CONTENT TO COPY");
      return;
    }
    try {
      await navigator.clipboard.writeText(generatedContent);
      toast.success("COPIED TO CLIPBOARD");
    } catch (error) {
      toast.error("COPY FAILED");
    }
  };

  // PDF Preview - generates preview without consuming credits
  const generatePdfPreview = async () => {
    const keywordsString = getKeywordsString();
    const validPhones = getValidPhoneNumbers();
    if (!keywordsString || !validPhones) {
      toast.error("AT LEAST ONE KEYWORD AND PHONE NUMBER REQUIRED");
      return;
    }

    setIsPreviewLoading(true);
    try {
      const response = await axios.post(`${API}/quickindex/preview-pdf`, {
        keywords: keywordsString,
        phone_number: validPhones,
        language,
        category: category,
        phone_region: phoneRegion,
        use_fancy_numbers: useFancyNumbers,
        use_emojis: useEmojis,
        emoji_density: emojiDensity,
        include_faq: includeFaq,
        title: title.trim() || keywordsString.split(",")[0].trim(),
        header_color: headerColor,
        logo_url: logoUrl,
      });

      if (response.data.success) {
        setPdfPreview(response.data.pdf_base64);
        toast.success("PDF PREVIEW READY - Click to view");
      }
    } catch (error) {
      console.error("PDF preview error:", error);
      toast.error("PREVIEW FAILED");
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // Open PDF preview in new tab
  const openPdfPreview = () => {
    if (pdfPreview) {
      const pdfWindow = window.open();
      pdfWindow.document.write(
        `<iframe width="100%" height="100%" src="data:application/pdf;base64,${pdfPreview}"></iframe>`
      );
    }
  };

  // AI Blog Generation
  const generateBlog = async () => {
    if (!blogTopic.trim()) {
      toast.error("PLEASE ENTER A BLOG TOPIC");
      return;
    }

    setIsGeneratingBlog(true);
    try {
      const token = localStorage.getItem("token");
      const validLinks = blogLinks.filter(l => l.text && l.url);
      
      const response = await axios.post(`${API}/generate-blog`, {
        topic: blogTopic.trim(),
        keywords: blogKeywords.trim(),
        links: validLinks,
        language: language,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setBlogContent(response.data.content);
      toast.success("BLOG GENERATED WITH AI!");
    } catch (error) {
      console.error("Blog generation error:", error);
      toast.error(error.response?.data?.detail || "BLOG GENERATION FAILED");
    } finally {
      setIsGeneratingBlog(false);
    }
  };

  const downloadBlogPDF = async () => {
    if (!blogContent) {
      toast.error("NO BLOG CONTENT TO DOWNLOAD");
      return;
    }

    setIsDownloading(true);
    try {
      const token = localStorage.getItem("token");
      const validLinks = blogLinks.filter(l => l.text && l.url);
      
      const response = await axios.post(
        `${API}/generate-blog-pdf`,
        {
          content: blogContent,
          topic: blogTopic.trim(),
          links: validLinks,
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
          timeout: 60000
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const topicSlug = blogTopic.trim().replace(/\s+/g, "_").slice(0, 30);
      link.setAttribute("download", `blog_${topicSlug}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("BLOG PDF DOWNLOADED WITH HYPERLINKS");
    } catch (error) {
      console.error("Blog PDF error:", error);
      toast.error("PDF DOWNLOAD FAILED");
    } finally {
      setIsDownloading(false);
    }
  };

  const clearAll = () => {
    setKeywords(INITIAL_KEYWORDS);
    setPhoneNumbers([""]);
    setTitle("");
    setLanguage("en");
    setPages(1);
    setPdfQuality("hd");
    setIncludeFaq(false);
    setFaqMode("auto");
    setCustomFaqs(INITIAL_FAQS);
    setGeneratedContent("");
    setWordCount(0);
    setUseFancyNumbers(true);
    setUseEmojis(true);
    setEmojiDensity(100);
    setUseAutoKeywords(false); // Reset to manual mode
    setContentSeed(""); // Reset content seed
    setBlogTopic("");
    setBlogKeywords("");
    setBlogLinks([{ text: "", url: "" }]);
    setBlogContent("");
    toast.info("CLEARED");
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-[#09090b] text-[#fafafa]' : 'bg-gray-100 text-gray-900'}`}>
      {/* Header */}
      <header className={`border-b ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'} p-3 md:p-6`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-[#ccff00] flex items-center justify-center rounded-lg">
              <Terminal className="w-5 h-5 md:w-6 md:h-6 text-black" />
            </div>
            <div>
              <h1 className="font-bold text-lg md:text-2xl tracking-tight uppercase">ContentWriter.World</h1>
              <p className={`text-[8px] md:text-[10px] ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'} tracking-widest uppercase hidden sm:block`}>
                SEO CONTENT GENERATOR
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            {/* Credits Display */}
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border`}>
              <Zap className="w-4 h-4 text-[#ccff00]" />
              <span className="font-bold text-[#ccff00]">{credits}</span>
              <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>credits</span>
            </div>
            {/* Theme Toggle */}
            <button 
              onClick={toggleTheme}
              className="p-1.5 md:p-2 rounded hover:bg-gray-500/20">
              {theme === 'dark' ? <Sun className="w-4 h-4 md:w-5 md:h-5" /> : <Moon className="w-4 h-4 md:w-5 md:h-5" />}
            </button>
            {/* User Info */}
            <div className={`hidden md:flex items-center gap-2 px-3 py-1 ${theme === 'dark' ? 'bg-[#18181b]' : 'bg-white'} border ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'} rounded-lg`}>
              <User className="w-4 h-4 text-[#ccff00]" />
              <span className="text-sm">{user?.name || user?.email?.split('@')[0]}</span>
            </div>
            <Button onClick={onLogout} variant="outline" size="sm" className={`${theme === 'dark' ? 'border-[#3f3f46]' : ''}`}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* QuickIndex.Pro Credits Banner */}
      <div className="bg-gradient-to-r from-[#ccff00] to-[#a3cc00] text-black py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-4 text-sm md:text-base">
          <Zap className="w-5 h-5" />
          <span className="font-bold">QuickIndex.Pro Credits: {credits}</span>
          <span className="hidden sm:inline">• 1 credit = 1 PDF</span>
          <a 
            href="https://quickindex.pro" 
            target="_blank" 
            rel="noopener noreferrer"
            className="underline font-bold hover:no-underline"
          >
            Get More Credits
          </a>
        </div>
      </div>

      {/* OLD Subscription Countdown Banner - DISABLED */}
      {false && subscriptionTimeLeft && (
        <div className={`${
          subscriptionTimeLeft.expired ? 'bg-red-500' : 
          subscriptionTimeLeft.type === 'trial' ? 
            (subscriptionTimeLeft.hours < 6 ? 'bg-red-500/90' : subscriptionTimeLeft.hours < 24 ? 'bg-orange-500' : 'bg-[#ccff00]') :
            (subscriptionTimeLeft.days < 3 ? 'bg-red-500/90' : subscriptionTimeLeft.days < 7 ? 'bg-orange-500' : 'bg-[#ccff00]')
        } text-black py-3 px-4`}>
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-4 flex-wrap">
            <Clock className="w-5 h-5" />
            {subscriptionTimeLeft.expired ? (
              <span className="font-bold text-white">Your subscription has expired! Please renew to continue.</span>
            ) : subscriptionTimeLeft.type === 'trial' ? (
              /* Trial shows hours:minutes:seconds */
              <>
                <span className="font-bold">TRIAL EXPIRES IN:</span>
                <div className="flex items-center gap-2">
                  <div className="bg-black/20 px-3 py-1 rounded">
                    <span className="font-mono font-bold text-lg">{String(subscriptionTimeLeft.hours + (subscriptionTimeLeft.days * 24)).padStart(2, '0')}</span>
                    <span className="text-xs ml-1">HRS</span>
                  </div>
                  <span className="font-bold">:</span>
                  <div className="bg-black/20 px-3 py-1 rounded">
                    <span className="font-mono font-bold text-lg">{String(subscriptionTimeLeft.minutes).padStart(2, '0')}</span>
                    <span className="text-xs ml-1">MIN</span>
                  </div>
                  <span className="font-bold">:</span>
                  <div className="bg-black/20 px-3 py-1 rounded">
                    <span className="font-mono font-bold text-lg">{String(subscriptionTimeLeft.seconds).padStart(2, '0')}</span>
                    <span className="text-xs ml-1">SEC</span>
                  </div>
                </div>
                <Button size="sm" className="bg-black text-white hover:bg-gray-800" onClick={onUpgrade}>
                  Upgrade Now
                </Button>
              </>
            ) : (
              /* Monthly shows days remaining */
              <>
                <span className="font-bold">SUBSCRIPTION EXPIRES IN:</span>
                <div className="flex items-center gap-2">
                  <div className="bg-black/20 px-4 py-2 rounded">
                    <span className="font-mono font-bold text-2xl">{subscriptionTimeLeft.days}</span>
                    <span className="text-sm ml-2">{subscriptionTimeLeft.days === 1 ? 'DAY' : 'DAYS'}</span>
                  </div>
                  {subscriptionTimeLeft.days < 7 && (
                    <>
                      <div className="bg-black/20 px-3 py-1 rounded">
                        <span className="font-mono font-bold">{String(subscriptionTimeLeft.hours).padStart(2, '0')}</span>
                        <span className="text-xs ml-1">HRS</span>
                      </div>
                    </>
                  )}
                </div>
                {subscriptionTimeLeft.days < 7 && (
                  <Button size="sm" className="bg-black text-white hover:bg-gray-800" onClick={onUpgrade}>
                    Renew Now
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel - Controls */}
          <div className="space-y-6">
            <div className={`${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border p-6`}>
              <div className="flex items-center gap-2 mb-6">
                <Zap className="w-4 h-4 text-[#ccff00]" />
                <h2 className="font-bold text-lg tracking-tight uppercase">INPUT PARAMETERS</h2>
              </div>

              {/* Category Selection */}
              <div className="space-y-2 mb-6">
                <Label className={`text-[10px] font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'} flex items-center gap-2`}>
                  <FileStack className="w-3 h-3" /> CATEGORY
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: "airlines", label: "Airlines", icon: "✈️" },
                    { id: "crypto", label: "Crypto", icon: "₿" },
                    { id: "car_rental", label: "Car Rental", icon: "🚗" },
                  ].map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setCategory(cat.id)}
                      className={`p-3 border-2 transition-all uppercase font-bold text-xs flex flex-col items-center gap-1 relative ${
                        category === cat.id 
                          ? "border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]" 
                          : theme === 'dark' ? "border-[#3f3f46] text-[#a1a1aa] hover:border-[#52525b]" : "border-gray-200 hover:border-gray-300"
                      }`}
                      data-testid={`category-${cat.id}`}
                    >
                      <span className="text-lg">{cat.icon}</span>
                      <span>{cat.label}</span>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-green-500 mt-1">
                  🎉 All categories FREE until September 1, 2026
                </p>
              </div>

              {/* Title */}
              <div className="space-y-2 mb-6">
                <Label className={`text-[10px] font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'} flex items-center gap-2`}>
                  <Type className="w-3 h-3" /> TITLE (OPTIONAL)
                </Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={
                    category === "crypto" 
                      ? "Coinbase Customer Support USA" 
                      : category === "car_rental" 
                        ? "Enterprise Rent-A-Car México" 
                        : "American Airlines Teléfono Oficial en México"
                  }
                  className={`${theme === 'dark' ? 'bg-[#27272a]/50 border-0 border-b-2 border-[#3f3f46]' : 'border-gray-200'} focus:border-[#ccff00] rounded-none`}
                />
              </div>

              {/* Keywords */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between">
                  <Label className={`text-[10px] font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'} flex items-center gap-2`}>
                    <Hash className="w-3 h-3" /> KEYWORDS (UP TO 10)
                  </Label>
                  <Button 
                    onClick={autoFetchKeywords} 
                    size="sm" 
                    className="bg-[#ccff00] text-black text-xs h-7 px-3"
                  >
                    <Sparkles className="w-3 h-3 mr-1" /> Auto Fill
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {keywords.map((keyword, index) => (
                    <Input
                      key={index}
                      value={keyword}
                      onChange={(e) => updateKeyword(index, e.target.value)}
                      placeholder={`Keyword ${index + 1}`}
                      className={`${theme === 'dark' ? 'bg-[#27272a]/50 border-0 border-b-2 border-[#3f3f46]' : 'border-gray-200'} focus:border-[#ccff00] rounded-none text-xs p-3`}
                    />
                  ))}
                </div>
              </div>

              {/* Phone Numbers with Region Selector (up to 3) */}
              <div className="space-y-2 mb-6">
                <div className="flex items-center justify-between">
                  <Label className={`text-[10px] font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'} flex items-center gap-2`}>
                    <Phone className="w-3 h-3" /> PHONE NUMBERS ({phoneNumbers.length}/3)
                  </Label>
                  {phoneNumbers.length < 3 && (
                    <button
                      onClick={addPhoneNumber}
                      className="flex items-center gap-1 text-[#ccff00] text-xs font-bold hover:bg-[#ccff00]/10 px-2 py-1 transition-all"
                    >
                      <Plus className="w-3 h-3" /> ADD
                    </button>
                  )}
                </div>
                
                {phoneNumbers.map((phone, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <Input
                      value={phone}
                      onChange={(e) => updatePhoneNumber(index, e.target.value)}
                      placeholder={`Phone ${index + 1}: +1-800-123-4567`}
                      className={`flex-1 ${theme === 'dark' ? 'bg-[#27272a]/50 border-0 border-b-2 border-[#3f3f46]' : 'border-gray-200'} focus:border-[#ccff00] rounded-none`}
                    />
                    {index === 0 ? (
                      <Select value={phoneRegion} onValueChange={setPhoneRegion}>
                        <SelectTrigger className={`w-24 ${theme === 'dark' ? 'bg-[#27272a]/50 border-0 border-b-2 border-[#3f3f46]' : 'border-gray-200'} rounded-none`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : ''}>
                          <SelectItem value="mx">🇲🇽 MX</SelectItem>
                          <SelectItem value="us">🇺🇸 US</SelectItem>
                          <SelectItem value="es">🇪🇸 ES</SelectItem>
                          <SelectItem value="uk">🇬🇧 UK</SelectItem>
                          <SelectItem value="other">🌍 Other</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <button
                        onClick={() => removePhoneNumber(index)}
                        className="w-24 h-9 flex items-center justify-center text-red-500 hover:bg-red-500/10 border-b-2 border-[#3f3f46] transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                
                <p className={`text-[10px] ${theme === 'dark' ? 'text-[#71717a]' : 'text-gray-400'}`}>
                  Numbers will show with ({phoneRegion.toUpperCase()}) tag • All numbers used in content
                </p>
              </div>

              {/* Language */}
              <div className="space-y-2 mb-6">
                <Label className={`text-[10px] font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'} flex items-center gap-2`}>
                  <Globe className="w-3 h-3" /> LANGUAGE
                </Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className={`${theme === 'dark' ? 'bg-[#27272a]/50 border-0 border-b-2 border-[#3f3f46]' : 'border-gray-200'} rounded-none`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : ''}>
                    {LANGUAGES.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>{lang.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Fancy Numbers Toggle */}
              <div className="space-y-2 mb-4">
                <button
                  onClick={() => setUseFancyNumbers(!useFancyNumbers)}
                  className={`w-full p-3 border-2 transition-all uppercase font-bold text-xs flex items-center justify-between ${
                    useFancyNumbers ? "border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]" : theme === 'dark' ? "border-[#3f3f46] text-[#a1a1aa]" : "border-gray-200"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Hash className="w-3 h-3" /> FANCY NUMBERS (𝟏𝟐𝟑)
                  </span>
                  <span className={`w-10 h-5 relative ${useFancyNumbers ? "bg-[#ccff00]" : theme === 'dark' ? "bg-[#3f3f46]" : "bg-gray-200"}`}>
                    <span className={`absolute top-0.5 w-4 h-4 bg-black transition-all ${useFancyNumbers ? "right-0.5" : "left-0.5"}`}></span>
                  </span>
                </button>
              </div>

              {/* Emoji Toggle */}
              <div className="space-y-2 mb-4">
                <button
                  onClick={() => setUseEmojis(!useEmojis)}
                  className={`w-full p-3 border-2 transition-all uppercase font-bold text-xs flex items-center justify-between ${
                    useEmojis ? "border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]" : theme === 'dark' ? "border-[#3f3f46] text-[#a1a1aa]" : "border-gray-200"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Type className="w-3 h-3" /> EMOJIS (📞✅🔥)
                  </span>
                  <span className={`w-10 h-5 relative ${useEmojis ? "bg-[#ccff00]" : theme === 'dark' ? "bg-[#3f3f46]" : "bg-gray-200"}`}>
                    <span className={`absolute top-0.5 w-4 h-4 bg-black transition-all ${useEmojis ? "right-0.5" : "left-0.5"}`}></span>
                  </span>
                </button>
              </div>

              {/* Emoji Density - only show when emojis are ON */}
              {useEmojis && (
                <div className="space-y-2 mb-6">
                  <Label className={`text-[10px] font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'}`}>
                    EMOJI DENSITY
                  </Label>
                  <div className="grid grid-cols-4 gap-2">
                    {[30, 50, 70, 100].map((density) => (
                      <button
                        key={density}
                        onClick={() => setEmojiDensity(density)}
                        className={`p-2 border-2 text-xs font-bold transition-all ${
                          emojiDensity === density 
                            ? "border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]" 
                            : theme === 'dark' ? "border-[#3f3f46] text-[#a1a1aa]" : "border-gray-200"
                        }`}
                      >
                        {density}%
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Meta Description Section */}
              <div className="space-y-3 mb-6">
                <Label className={`text-[10px] font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'} flex items-center gap-2`}>
                  <FileText className="w-3 h-3" /> META DESCRIPTION
                </Label>
                <div className="flex gap-2 mb-2">
                  <button
                    onClick={() => setMetaMode("auto")}
                    className={`flex-1 p-2 text-sm font-bold ${metaMode === "auto" ? "bg-[#ccff00] text-black" : theme === 'dark' ? "bg-[#27272a]" : "bg-gray-100"}`}
                  >
                    AUTO GENERATE
                  </button>
                  <button
                    onClick={() => setMetaMode("manual")}
                    className={`flex-1 p-2 text-sm font-bold ${metaMode === "manual" ? "bg-[#ccff00] text-black" : theme === 'dark' ? "bg-[#27272a]" : "bg-gray-100"}`}
                  >
                    MANUAL
                  </button>
                </div>
                {metaMode === "manual" && (
                  <Textarea
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                    placeholder="Enter your custom meta description (150-160 characters recommended)..."
                    className={`text-sm ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : ''}`}
                    rows={3}
                    maxLength={200}
                  />
                )}
                {metaMode === "auto" && (
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                    Meta description will be auto-generated based on keywords and phone number.
                  </p>
                )}
              </div>

              {/* FAQ Toggle - FREE ACCESS for all users */}
              <div className="space-y-2 mb-6">
                <Label className={`text-[10px] font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'} flex items-center gap-2`}>
                  <HelpCircle className="w-3 h-3" /> FAQ (PAGES 1-3)
                  <span className="bg-green-500 text-white text-[8px] px-1.5 py-0.5 font-bold ml-2">FREE</span>
                </Label>
                <button
                  onClick={() => setIncludeFaq(!includeFaq)}
                  className={`w-full p-4 border-2 transition-all uppercase font-bold text-sm flex items-center justify-between ${
                    includeFaq ? "border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]" : theme === 'dark' ? "border-[#3f3f46] text-[#a1a1aa]" : "border-gray-200"
                  }`}
                >
                  <span>INCLUDE FAQ</span>
                  <span className={`w-12 h-6 relative ${includeFaq ? "bg-[#ccff00]" : theme === 'dark' ? "bg-[#3f3f46]" : "bg-gray-200"}`}>
                    <span className={`absolute top-1 w-4 h-4 bg-black transition-all ${includeFaq ? "right-1" : "left-1"}`}></span>
                  </span>
                </button>

                {includeFaq && (
                  <div className="mt-4 space-y-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setFaqMode("auto")}
                        className={`flex-1 p-2 text-sm font-bold ${faqMode === "auto" ? "bg-[#ccff00] text-black" : theme === 'dark' ? "bg-[#27272a]" : "bg-gray-100"}`}
                      >
                        AUTO FETCH
                      </button>
                      <button
                        onClick={() => setFaqMode("custom")}
                        className={`flex-1 p-2 text-sm font-bold ${faqMode === "custom" ? "bg-[#ccff00] text-black" : theme === 'dark' ? "bg-[#27272a]" : "bg-gray-100"}`}
                      >
                        CUSTOM (5)
                      </button>
                    </div>

                    {faqMode === "custom" && (
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {customFaqs.map((faq, index) => (
                          <div key={index} className={`p-3 ${theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-50'}`}>
                            <Label className="text-xs mb-1 block">Q{index + 1}</Label>
                            <Input
                              value={faq.question}
                              onChange={(e) => updateFaq(index, "question", e.target.value)}
                              placeholder="Question..."
                              className={`mb-2 text-sm ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : ''}`}
                            />
                            <Label className="text-xs mb-1 block">A{index + 1}</Label>
                            <Textarea
                              value={faq.answer}
                              onChange={(e) => updateFaq(index, "answer", e.target.value)}
                              placeholder="Answer..."
                              className={`text-sm ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : ''}`}
                              rows={2}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Pages Slider */}
              <div className="space-y-4 mb-8">
                <Label className={`text-[10px] font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'} flex items-center gap-2`}>
                  <FileStack className="w-3 h-3" /> PDF PAGES: <span className="text-[#ccff00]">{pages}</span>
                </Label>
                <Slider
                  value={[pages]}
                  onValueChange={(value) => setPages(value[0])}
                  min={1}
                  max={100}
                  step={1}
                />
                <div className={`flex justify-between text-[10px] ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'}`}>
                  <span>1</span><span>50</span><span>100</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={generateContent} disabled={isGenerating} className="bg-[#ccff00] text-black hover:bg-[#ccff00]/90 h-12">
                  {isGenerating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                  {isGenerating ? "PROCESSING..." : "GENERATE"}
                </Button>
                <Button onClick={clearAll} variant="outline" className={`h-12 ${theme === 'dark' ? 'border-[#3f3f46]' : ''}`}>
                  <RefreshCw className="w-4 h-4 mr-2" /> CLEAR
                </Button>
              </div>
            </div>

            {/* Export Panel */}
            <div className={`${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border p-6`}>
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-4 h-4 text-[#ccff00]" />
                <h2 className="font-bold text-lg uppercase">EXPORT</h2>
              </div>

              {/* PDF Customization */}
              <div className={`p-4 mb-4 rounded-lg ${theme === 'dark' ? 'bg-[#27272a] border-[#3f3f46]' : 'bg-gray-100 border-gray-200'} border`}>
                <div className="flex items-center gap-2 mb-3">
                  <Palette className="w-4 h-4 text-[#ccff00]" />
                  <span className="font-bold text-sm uppercase">PDF STYLING</span>
                </div>
                
                {/* Header Color */}
                <div className="mb-3">
                  <Label className="text-xs mb-1 block">Header Color:</Label>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { id: "blue", color: "bg-blue-500", label: "Blue" },
                      { id: "green", color: "bg-green-500", label: "Green" },
                      { id: "red", color: "bg-red-500", label: "Red" },
                      { id: "purple", color: "bg-purple-500", label: "Purple" },
                      { id: "orange", color: "bg-orange-500", label: "Orange" },
                    ].map((c) => (
                      <button
                        key={c.id}
                        onClick={() => setHeaderColor(c.id)}
                        className={`w-8 h-8 rounded-lg ${c.color} ${headerColor === c.id ? 'ring-2 ring-[#ccff00] ring-offset-2 ring-offset-[#18181b]' : ''} transition-all`}
                        title={c.label}
                      />
                    ))}
                  </div>
                </div>
                
                {/* Logo URL */}
                <div>
                  <Label className="text-xs mb-1 block">Company Logo URL (optional):</Label>
                  <Input
                    type="url"
                    value={logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    placeholder="https://example.com/logo.png"
                    className={`h-9 text-xs ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46] text-white' : ''}`}
                  />
                </div>
              </div>

              {/* PDF Preview Button */}
              <Button 
                onClick={generatePdfPreview} 
                disabled={isPreviewLoading}
                className={`w-full h-10 mb-3 ${theme === 'dark' ? 'bg-[#27272a] hover:bg-[#3f3f46] border-[#3f3f46]' : 'bg-gray-100 hover:bg-gray-200'} text-inherit border`}
              >
                <Eye className="w-4 h-4 mr-2" /> {isPreviewLoading ? "Generating Preview..." : "Preview PDF (FREE)"}
              </Button>
              
              {/* PDF Preview Viewer */}
              {pdfPreview && (
                <div className={`mb-3 p-3 rounded-lg ${theme === 'dark' ? 'bg-green-500/10 border-green-500/50' : 'bg-green-50 border-green-200'} border`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-green-500 font-bold">✓ Preview Ready</span>
                    <Button onClick={openPdfPreview} size="sm" className="bg-green-500 text-white h-7 text-xs">
                      <ExternalLink className="w-3 h-3 mr-1" /> Open Preview
                    </Button>
                  </div>
                </div>
              )}

              {/* Single PDF Download */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <Button 
                  onClick={downloadPDF} 
                  disabled={!generatedContent || isDownloading} 
                  className={`h-12 ${theme === 'dark' ? 'bg-[#27272a] hover:bg-[#3f3f46] border-[#3f3f46]' : 'bg-gray-100 hover:bg-gray-200'} text-inherit`}
                >
                  <Download className="w-4 h-4 mr-2" /> {isDownloading ? "..." : "HD PDF (1 credit)"}
                </Button>
                <Button onClick={copyToClipboard} disabled={!generatedContent} className={`h-12 ${theme === 'dark' ? 'bg-[#27272a] hover:bg-[#3f3f46] border-[#3f3f46]' : 'bg-gray-100 hover:bg-gray-200'} text-inherit`}>
                  <Copy className="w-4 h-4 mr-2" /> COPY
                </Button>
              </div>

              {/* Bulk PDF Generation */}
              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-[#27272a] border-[#3f3f46]' : 'bg-gray-100 border-gray-200'} border`}>
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-[#ccff00]" />
                  <span className="font-bold text-sm uppercase">BULK HD PDF GENERATION</span>
                  <span className="text-[8px] bg-blue-500 text-white px-1.5 py-0.5 rounded font-bold">HD</span>
                </div>
                <p className={`text-xs mb-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Generate 1-20 unique HD PDFs with emojis, fancy numbers & styled titles
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">PDFs:</Label>
                    <select
                      value={bulkCount}
                      onChange={(e) => setBulkCount(parseInt(e.target.value))}
                      className={`w-20 h-10 px-2 rounded-lg ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46] text-white' : 'bg-white border-gray-200'} border text-center font-bold`}
                    >
                      {[...Array(20)].map((_, i) => (
                        <option key={i+1} value={i+1}>{i+1}</option>
                      ))}
                    </select>
                  </div>
                  <Button 
                    onClick={downloadBulkPDFs} 
                    disabled={isBulkGenerating || credits < bulkCount}
                    className="flex-1 h-10 bg-[#ccff00] text-black hover:bg-[#b8e600] font-bold"
                  >
                    {isBulkGenerating ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating HD...</>
                    ) : (
                      <><Download className="w-4 h-4 mr-2" /> {bulkCount} HD PDFs ({bulkCount} credits)</>
                    )}
                  </Button>
                </div>
                {credits < bulkCount && (
                  <p className="text-xs text-red-400 mt-2">
                    Need {bulkCount} credits, have {credits}. <a href="https://quickindex.pro" target="_blank" rel="noopener noreferrer" className="underline">Get more →</a>
                  </p>
                )}
              </div>
              
              {/* Google Docs tip */}
              <div className={`mt-3 p-3 text-xs ${theme === 'dark' ? 'bg-[#27272a] text-[#a1a1aa]' : 'bg-gray-100 text-gray-600'} rounded`}>
                <span className="text-[#ccff00] font-bold">💡 TIP:</span> Copy content and paste to <a href="https://docs.google.com" target="_blank" rel="noopener noreferrer" className="text-[#ccff00] underline">Google Docs</a> for formatting
              </div>
            </div>

            {/* AI Blog Quick Access - DISABLED */}
            {/* <button 
              onClick={onAIBlog}
              className={`w-full ${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border p-6 flex items-center justify-between hover:border-purple-500/50 transition-all group`}
            >
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-purple-400 group-hover:animate-pulse" />
                <div className="text-left">
                  <h2 className="font-bold text-lg uppercase">AI BLOG GENERATOR</h2>
                  <p className={`text-xs ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'}`}>Generate SEO blogs with AI</p>
                </div>
                <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white text-[10px] px-2 py-1 font-bold rounded animate-pulse">NEW</span>
              </div>
              <ChevronRight className="w-5 h-5 text-purple-400" />
            </button> */}
          </div>

          {/* Right Panel - Preview */}
          <div className={`${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border`}>
            <div className={`border-b ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'} p-4 flex items-center justify-between`}>
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#ccff00]" />
                <h2 className="font-bold text-lg uppercase">PREVIEW</h2>
              </div>
              {wordCount > 0 && (
                <div className={`text-[10px] ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'} uppercase`}>
                  {wordCount.toLocaleString()} WORDS • ~{Math.ceil(wordCount / 500)} PAGES
                </div>
              )}
            </div>
            
            <ScrollArea className="h-[600px]">
              <div className="p-6">
                {generatedContent ? (
                  <div className={`font-mono text-xs leading-relaxed ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-600'} whitespace-pre-wrap break-words`}>
                    {generatedContent}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[400px] text-center">
                    <div className={`w-16 h-16 border-2 border-dashed ${theme === 'dark' ? 'border-[#3f3f46]' : 'border-gray-200'} flex items-center justify-center mb-4`}>
                      <Terminal className={`w-8 h-8 ${theme === 'dark' ? 'text-[#3f3f46]' : 'text-gray-300'}`} />
                    </div>
                    <p className={`${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'} text-sm uppercase`}>NO CONTENT GENERATED</p>
                    <p className={`${theme === 'dark' ? 'text-[#3f3f46]' : 'text-gray-300'} text-xs uppercase mt-1`}>ENTER PARAMETERS AND CLICK GENERATE</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </main>
    </div>
  );
}


// ==================== USER PROFILE PAGE ====================
function ProfilePage({ user, theme, onBack, onUpdateUser }) {
  const [name, setName] = useState(user?.name || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const updateProfile = async () => {
    if (!name.trim()) {
      toast.error("NAME IS REQUIRED");
      return;
    }
    
    setIsUpdating(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/auth/profile`, {
        name: name.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("PROFILE UPDATED");
      if (onUpdateUser) onUpdateUser({ ...user, name: name.trim() });
    } catch (error) {
      toast.error(error.response?.data?.detail || "UPDATE FAILED");
    } finally {
      setIsUpdating(false);
    }
  };

  const changePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("ALL PASSWORD FIELDS REQUIRED");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("PASSWORDS DO NOT MATCH");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("PASSWORD MUST BE AT LEAST 6 CHARACTERS");
      return;
    }

    setIsUpdating(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("PASSWORD CHANGED");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "PASSWORD CHANGE FAILED");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
      <header className={`border-b ${theme === 'dark' ? 'border-[#3f3f46] bg-black/80' : 'border-gray-200 bg-white/80'} backdrop-blur-sm sticky top-0 z-50`}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-2 text-[#ccff00]">
            <ChevronRight className="w-4 h-4 rotate-180" /> BACK
          </button>
          <h1 className="font-bold text-xl">USER PROFILE</h1>
          <div></div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-8 space-y-6">
        {/* Profile Info */}
        <div className={`${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border p-6 rounded`}>
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-[#ccff00]" />
            <h2 className="font-bold text-lg">PROFILE INFO</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <Label className={`text-xs font-bold uppercase ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'}`}>EMAIL</Label>
              <Input value={user?.email || ""} disabled className={`mt-1 ${theme === 'dark' ? 'bg-[#27272a]/50' : 'bg-gray-100'} opacity-60`} />
            </div>
            
            <div>
              <Label className={`text-xs font-bold uppercase ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'}`}>USERNAME / DISPLAY NAME</Label>
              <Input 
                value={name} 
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                className={`mt-1 ${theme === 'dark' ? 'bg-[#27272a]/50 border-[#3f3f46]' : ''}`}
              />
            </div>

            <div>
              <Label className={`text-xs font-bold uppercase ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'}`}>SUBSCRIPTION</Label>
              <div className={`mt-1 p-3 ${theme === 'dark' ? 'bg-[#27272a]/50' : 'bg-gray-100'} rounded flex items-center gap-2`}>
                <CreditCard className="w-4 h-4 text-[#ccff00]" />
                <span className="font-bold uppercase">{user?.subscription_type || "FREE"}</span>
              </div>
            </div>
            
            <Button onClick={updateProfile} disabled={isUpdating} className="w-full bg-[#ccff00] text-black hover:bg-[#b8e600]">
              {isUpdating ? "UPDATING..." : "UPDATE PROFILE"}
            </Button>
          </div>
        </div>

        {/* Change Password */}
        <div className={`${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border p-6 rounded`}>
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-[#ccff00]" />
            <h2 className="font-bold text-lg">CHANGE PASSWORD</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <Label className={`text-xs font-bold uppercase ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'}`}>CURRENT PASSWORD</Label>
              <Input 
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className={`mt-1 ${theme === 'dark' ? 'bg-[#27272a]/50 border-[#3f3f46]' : ''}`}
              />
            </div>
            
            <div>
              <Label className={`text-xs font-bold uppercase ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'}`}>NEW PASSWORD</Label>
              <Input 
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className={`mt-1 ${theme === 'dark' ? 'bg-[#27272a]/50 border-[#3f3f46]' : ''}`}
              />
            </div>
            
            <div>
              <Label className={`text-xs font-bold uppercase ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'}`}>CONFIRM NEW PASSWORD</Label>
              <Input 
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className={`mt-1 ${theme === 'dark' ? 'bg-[#27272a]/50 border-[#3f3f46]' : ''}`}
              />
            </div>
            
            <Button onClick={changePassword} disabled={isUpdating} className="w-full bg-[#ccff00] text-black hover:bg-[#b8e600]">
              {isUpdating ? "CHANGING..." : "CHANGE PASSWORD"}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}


// ==================== SUB-USERS PAGE ====================
function SubUsersPage({ user, theme, onBack }) {
  const [subUsers, setSubUsers] = useState([]);
  const [maxSubUsers, setMaxSubUsers] = useState(0);
  const [remainingSlots, setRemainingSlots] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [createdUser, setCreatedUser] = useState(null);

  useEffect(() => {
    fetchSubUsers();
  }, []);

  const fetchSubUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/user/sub-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubUsers(response.data.sub_users || []);
      setMaxSubUsers(response.data.max_sub_users || 0);
      setRemainingSlots(response.data.remaining_slots || 0);
    } catch (error) {
      console.error("Error fetching sub-users:", error);
      if (error.response?.status === 403) {
        toast.error("You don't have permission to manage sub-users");
      }
    } finally {
      setLoading(false);
    }
  };

  const createSubUser = async () => {
    if (!newUsername.trim() || !newPassword.trim()) {
      toast.error("Please enter username and password");
      return;
    }
    
    setCreating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/user/create-sub-user`, {
        username: newUsername.trim(),
        password: newPassword.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCreatedUser(response.data.sub_user);
      toast.success("Sub-user created successfully!");
      fetchSubUsers();
      setNewUsername("");
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create sub-user");
    } finally {
      setCreating(false);
    }
  };

  const deleteSubUser = async (subUserId) => {
    if (!confirm("Are you sure you want to delete this sub-user?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/user/sub-user/${subUserId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Sub-user deleted");
      fetchSubUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete sub-user");
    }
  };

  const resetPassword = async (subUserId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/user/sub-user/${subUserId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`New password: ${response.data.new_password}`);
      setCreatedUser({ ...subUsers.find(s => s.id === subUserId), password: response.data.new_password });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  const toggleActive = async (subUserId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/user/sub-user/${subUserId}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchSubUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to toggle sub-user status");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-[#09090b]' : 'bg-gray-50'} flex items-center justify-center`}>
        <RefreshCw className="w-8 h-8 animate-spin text-[#ccff00]" />
      </div>
    );
  }

  if (maxSubUsers === 0) {
    return (
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-[#09090b] text-white' : 'bg-gray-50 text-gray-900'}`}>
        <header className={`${theme === 'dark' ? 'bg-[#18181b] border-[#27272a]' : 'bg-white border-gray-200'} border-b px-4 py-4`}>
          <div className="max-w-4xl mx-auto flex items-center gap-4">
            <button onClick={onBack} className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-[#27272a]' : 'hover:bg-gray-100'}`}>
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold">Sub-User Management</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className={`${theme === 'dark' ? 'bg-[#18181b] border-[#27272a]' : 'bg-white border-gray-200'} border rounded-xl p-8 text-center`}>
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-500" />
            <h2 className="text-xl font-bold mb-2">No Sub-User Access</h2>
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
              You don't have permission to create sub-users. Contact admin to get sub-user slots assigned to your account.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-[#09090b] text-white' : 'bg-gray-50 text-gray-900'}`}>
      <header className={`${theme === 'dark' ? 'bg-[#18181b] border-[#27272a]' : 'bg-white border-gray-200'} border-b px-4 py-4`}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-[#27272a]' : 'hover:bg-gray-100'}`}>
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold">Sub-User Management</h1>
          </div>
          <div className="text-sm">
            <span className="text-[#ccff00] font-bold">{subUsers.length}</span>
            <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}> / {maxSubUsers} users</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Create Sub-User Card */}
        {remainingSlots > 0 && (
          <div className={`${theme === 'dark' ? 'bg-[#18181b] border-[#27272a]' : 'bg-white border-gray-200'} border rounded-xl p-6`}>
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#ccff00]" />
              Create New Sub-User ({remainingSlots} slots remaining)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Username</label>
                <Input
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="Enter username"
                  className={theme === 'dark' ? 'bg-[#27272a] border-[#3f3f46]' : ''}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Password</label>
                <Input
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter password"
                  className={theme === 'dark' ? 'bg-[#27272a] border-[#3f3f46]' : ''}
                />
              </div>
            </div>
            <Button 
              onClick={createSubUser} 
              disabled={creating || !newUsername.trim() || !newPassword.trim()}
              className="bg-[#ccff00] text-black hover:bg-[#ccff00]/90"
            >
              {creating ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : <><UserPlus className="w-4 h-4 mr-2" /> Create Sub-User</>}
            </Button>
          </div>
        )}

        {/* Newly Created User Credentials */}
        {createdUser && (
          <div className={`${theme === 'dark' ? 'bg-green-500/10' : 'bg-green-50'} border border-green-500/30 rounded-xl p-4 md:p-6`}>
            <h3 className="text-lg font-bold mb-3 flex items-center gap-2 text-green-500">
              <Check className="w-5 h-5" />
              Sub-User Credentials
            </h3>
            <div className="space-y-2 mb-4">
              <div className={`flex items-center justify-between p-3 ${theme === 'dark' ? 'bg-[#09090b]' : 'bg-white'} rounded-lg`}>
                <div className="min-w-0 flex-1">
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Login Email:</span>
                  <p className="font-mono font-bold text-sm truncate">{createdUser.email || createdUser.login_email}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => copyToClipboard(createdUser.email || createdUser.login_email)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <div className={`flex items-center justify-between p-3 ${theme === 'dark' ? 'bg-[#09090b]' : 'bg-white'} rounded-lg`}>
                <div className="min-w-0 flex-1">
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Password:</span>
                  <p className="font-mono font-bold">{createdUser.password}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => copyToClipboard(createdUser.password)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setCreatedUser(null)}>
              Dismiss
            </Button>
          </div>
        )}

        {/* Sub-Users List */}
        <div className={`${theme === 'dark' ? 'bg-[#18181b] border-[#27272a]' : 'bg-white border-gray-200'} border rounded-xl p-6`}>
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#ccff00]" />
            Your Sub-Users ({subUsers.length})
          </h2>
          
          {subUsers.length === 0 ? (
            <p className={`text-center py-8 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
              No sub-users created yet. Create your first sub-user above.
            </p>
          ) : (
            <div className="space-y-3">
              {subUsers.map((subUser) => (
                <div key={subUser.id} className={`${theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-100'} rounded-lg p-4`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${subUser.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                        <User className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold truncate">{subUser.username}</p>
                        <p className={`text-sm truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{subUser.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-13 sm:ml-0">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${subUser.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                        {subUser.is_active ? 'ACTIVE' : 'DISABLED'}
                      </span>
                      {subUser.active_sessions > 0 && (
                        <span className="px-2 py-1 rounded text-xs font-bold bg-blue-500/20 text-blue-500">
                          ONLINE
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={`flex flex-wrap items-center gap-2 mt-3 pt-3 border-t ${theme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
                    <Button size="sm" variant="outline" onClick={() => resetPassword(subUser.id)} className="text-xs">
                      <Key className="w-3 h-3 mr-1" /> Reset
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toggleActive(subUser.id)} className="text-xs">
                      {subUser.is_active ? <><Ban className="w-3 h-3 mr-1" /> Disable</> : <><Check className="w-3 h-3 mr-1" /> Enable</>}
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-500 hover:bg-red-500/10 text-xs" onClick={() => deleteSubUser(subUser.id)}>
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}


// ==================== AI BLOG PAGE ====================
function AIBlogPage({ user, theme, onBack }) {
  const [blogTopic, setBlogTopic] = useState("");
  const [blogKeywords, setBlogKeywords] = useState("");
  const [blogLinks, setBlogLinks] = useState([{ text: "", url: "" }]);
  const [blogContent, setBlogContent] = useState("");
  const [isGeneratingBlog, setIsGeneratingBlog] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [language, setLanguage] = useState("en");
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState("");

  const addBlogLink = () => {
    if (blogLinks.length < 5) {
      setBlogLinks([...blogLinks, { text: "", url: "" }]);
    }
  };

  const removeBlogLink = (index) => {
    if (blogLinks.length > 1) {
      setBlogLinks(blogLinks.filter((_, i) => i !== index));
    }
  };

  const updateBlogLink = (index, field, value) => {
    const newLinks = [...blogLinks];
    newLinks[index][field] = value;
    setBlogLinks(newLinks);
  };

  const generateBlog = async () => {
    if (!blogTopic.trim()) {
      toast.error("PLEASE ENTER A BLOG TOPIC");
      return;
    }

    setIsGeneratingBlog(true);
    setBlogContent("");
    setProgress(0);
    setProgressText("Initializing AI...");
    
    // Simulate progress while waiting for AI
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev < 15) {
          setProgressText("Connecting to AI service...");
          return prev + 3;
        } else if (prev < 35) {
          setProgressText("Analyzing topic and keywords...");
          return prev + 2;
        } else if (prev < 55) {
          setProgressText("Generating content structure...");
          return prev + 1.5;
        } else if (prev < 75) {
          setProgressText("Writing blog paragraphs...");
          return prev + 1;
        } else if (prev < 90) {
          setProgressText("Optimizing for SEO...");
          return prev + 0.5;
        } else if (prev < 95) {
          setProgressText("Finalizing content...");
          return prev + 0.2;
        }
        return prev;
      });
    }, 500);
    
    try {
      const token = localStorage.getItem("token");
      const validLinks = blogLinks.filter(l => l.text && l.url);
      
      const response = await axios.post(`${API}/generate-blog`, {
        topic: blogTopic.trim(),
        keywords: blogKeywords.trim(),
        links: validLinks,
        language: language,
        timestamp: Date.now(),
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 180000, // 3 minute timeout for AI generation
      });

      clearInterval(progressInterval);
      setProgress(100);
      setProgressText("Complete!");

      if (response.data && response.data.content) {
        setBlogContent(response.data.content);
        toast.success("BLOG GENERATED WITH AI!");
      } else {
        toast.error("NO CONTENT RECEIVED");
      }
    } catch (error) {
      clearInterval(progressInterval);
      setProgress(0);
      setProgressText("");
      console.error("Blog generation error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "BLOG GENERATION FAILED";
      toast.error(errorMsg);
      
      if (errorMsg.includes("Budget") || errorMsg.includes("budget")) {
        toast.error("LLM BUDGET EXCEEDED - Please add credits");
      }
    } finally {
      setIsGeneratingBlog(false);
      setTimeout(() => {
        setProgress(0);
        setProgressText("");
      }, 2000);
    }
  };

  const downloadBlogPDF = async () => {
    if (!blogContent) {
      toast.error("NO BLOG CONTENT");
      return;
    }

    setIsDownloading(true);
    try {
      const token = localStorage.getItem("token");
      const validLinks = blogLinks.filter(l => l.text && l.url);
      
      const response = await axios.post(`${API}/generate-blog-pdf`, {
        content: blogContent,
        topic: blogTopic.trim(),
        links: validLinks,
      }, { 
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
        timeout: 60000
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `blog_${blogTopic.slice(0, 20).replace(/\s+/g, "_")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF DOWNLOADED");
    } catch (error) {
      toast.error("PDF DOWNLOAD FAILED");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
      <header className={`border-b ${theme === 'dark' ? 'border-[#3f3f46] bg-black/80' : 'border-gray-200 bg-white/80'} backdrop-blur-sm sticky top-0 z-50`}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-2 text-[#ccff00]">
            <ChevronRight className="w-4 h-4 rotate-180" /> BACK
          </button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-400" />
            <h1 className="font-bold text-xl">AI BLOG GENERATOR</h1>
            <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[10px] px-2 py-0.5 font-bold rounded animate-pulse">AI</span>
          </div>
          <div></div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className={`${theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : 'bg-white border-gray-200'} border p-6 rounded space-y-6`}>
          {/* Blog Topic */}
          <div className="space-y-2">
            <Label className={`text-xs font-bold uppercase ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'}`}>BLOG TOPIC *</Label>
            <Input value={blogTopic} onChange={(e) => setBlogTopic(e.target.value)} placeholder="e.g., How to Contact Delta Airlines" className={theme === 'dark' ? 'bg-[#27272a]/50 border-[#3f3f46]' : ''} />
          </div>

          {/* Keywords */}
          <div className="space-y-2">
            <Label className={`text-xs font-bold uppercase ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'}`}>SEO KEYWORDS</Label>
            <Input value={blogKeywords} onChange={(e) => setBlogKeywords(e.target.value)} placeholder="customer service, phone, support" className={theme === 'dark' ? 'bg-[#27272a]/50 border-[#3f3f46]' : ''} />
          </div>

          {/* Language */}
          <div className="space-y-2">
            <Label className={`text-xs font-bold uppercase ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'}`}>LANGUAGE</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className={theme === 'dark' ? 'bg-[#27272a]/50 border-[#3f3f46]' : ''}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={theme === 'dark' ? 'bg-[#18181b] border-[#3f3f46]' : ''}>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="es">Español</SelectItem>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="de">Deutsch</SelectItem>
                <SelectItem value="pt">Português</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Links */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className={`text-xs font-bold uppercase ${theme === 'dark' ? 'text-[#a1a1aa]' : 'text-gray-500'}`}>HYPERLINKS ({blogLinks.length}/5)</Label>
              {blogLinks.length < 5 && <button onClick={addBlogLink} className="text-[#ccff00] text-xs font-bold flex items-center gap-1"><Plus className="w-3 h-3" /> ADD</button>}
            </div>
            {blogLinks.map((link, i) => (
              <div key={i} className="flex gap-2">
                <Input value={link.text} onChange={(e) => updateBlogLink(i, 'text', e.target.value)} placeholder="Link Text" className={`flex-1 text-sm ${theme === 'dark' ? 'bg-[#27272a]/50 border-[#3f3f46]' : ''}`} />
                <Input value={link.url} onChange={(e) => updateBlogLink(i, 'url', e.target.value)} placeholder="https://..." className={`flex-1 text-sm ${theme === 'dark' ? 'bg-[#27272a]/50 border-[#3f3f46]' : ''}`} />
                {blogLinks.length > 1 && <button onClick={() => removeBlogLink(i)} className="text-red-500 p-2"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
          </div>

          {/* Generate */}
          <div className="space-y-3">
            <Button onClick={generateBlog} disabled={isGeneratingBlog || !blogTopic.trim()} className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white h-12">
              {isGeneratingBlog ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> GENERATING...</> : <><Sparkles className="w-4 h-4 mr-2" /> GENERATE AI BLOG</>}
            </Button>
            
            {/* Progress Bar */}
            {isGeneratingBlog && (
              <div className="space-y-2">
                <div className={`w-full h-3 ${theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-200'} rounded-full overflow-hidden`}>
                  <div 
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{progressText}</span>
                  <span className="text-[#ccff00] font-bold">{Math.round(progress)}%</span>
                </div>
              </div>
            )}
          </div>

          {/* Result */}
          {blogContent && (
            <div className="space-y-4 pt-4 border-t border-[#3f3f46]">
              <h3 className="font-bold flex items-center gap-2"><FileText className="w-4 h-4 text-[#ccff00]" /> GENERATED BLOG</h3>
              <div className={`max-h-[300px] overflow-y-auto p-4 ${theme === 'dark' ? 'bg-[#27272a]' : 'bg-gray-100'} rounded text-sm whitespace-pre-wrap`}>{blogContent}</div>
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={downloadBlogPDF} disabled={isDownloading} className={`h-10 ${theme === 'dark' ? 'bg-[#27272a] hover:bg-[#3f3f46]' : ''} text-inherit`}>
                  <Download className="w-4 h-4 mr-2" /> {isDownloading ? "..." : "PDF"}
                </Button>
                <Button onClick={async () => { await navigator.clipboard.writeText(blogContent); toast.success("COPIED"); }} className={`h-10 ${theme === 'dark' ? 'bg-[#27272a] hover:bg-[#3f3f46]' : ''} text-inherit`}>
                  <Copy className="w-4 h-4 mr-2" /> COPY
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}


// ==================== MAIN APP ====================
function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");
  const [page, setPage] = useState("landing"); // landing, auth, generator, admin
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(0);

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const activationCode = localStorage.getItem("activation_code");
    if (!activationCode) {
      setLoading(false);
      return;
    }

    try {
      // Check balance/validity with QuickIndex.Pro
      const res = await axios.get(`${API}/quickindex/balance?activation_code=${encodeURIComponent(activationCode)}`);
      
      if (res.data.success) {
        const storedEmail = localStorage.getItem("quickindex_email") || "User";
        setUser({
          email: storedEmail,
          name: storedEmail.split('@')[0],
          activation_code: activationCode
        });
        setCredits(res.data.credits);
        localStorage.setItem("quickindex_credits", res.data.credits);
        setPage("generator");
      } else {
        // Invalid activation code
        localStorage.removeItem("activation_code");
        localStorage.removeItem("quickindex_credits");
        localStorage.removeItem("quickindex_email");
      }
    } catch (error) {
      // Clear invalid credentials
      localStorage.removeItem("activation_code");
      localStorage.removeItem("quickindex_credits");
      localStorage.removeItem("quickindex_email");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    checkAuth();
  };

  const handleLogout = () => {
    localStorage.removeItem("activation_code");
    localStorage.removeItem("quickindex_credits");
    localStorage.removeItem("quickindex_email");
    setUser(null);
    setCredits(0);
    setPage("landing");
  };

  const refreshCredits = async () => {
    const activationCode = localStorage.getItem("activation_code");
    if (!activationCode) return;
    
    try {
      const res = await axios.get(`${API}/quickindex/balance?activation_code=${encodeURIComponent(activationCode)}`);
      if (res.data.success) {
        setCredits(res.data.credits);
        localStorage.setItem("quickindex_credits", res.data.credits);
      }
    } catch (error) {
      console.error("Failed to refresh credits");
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-[#09090b]' : 'bg-gray-100'}`}>
        <RefreshCw className={`w-8 h-8 animate-spin ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} />
      </div>
    );
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: theme === 'dark' ? '#18181b' : '#fff',
            border: theme === 'dark' ? '1px solid #3f3f46' : '1px solid #e5e7eb',
            color: theme === 'dark' ? '#fafafa' : '#111827',
          },
        }}
      />
      
      {page === "landing" && (
        <LandingPageEnhanced 
          onGetStarted={() => setPage("auth")} 
          theme={theme} 
          toggleTheme={toggleTheme} 
        />
      )}
      
      {page === "auth" && (
        <AuthPage 
          onLogin={handleLogin} 
          theme={theme} 
          toggleTheme={toggleTheme} 
        />
      )}
      
      {page === "generator" && (
        <GeneratorPage 
          user={user} 
          credits={credits}
          refreshCredits={refreshCredits}
          theme={theme} 
          toggleTheme={toggleTheme} 
          onLogout={handleLogout}
          onReferral={() => {}}
          onUpgrade={() => {}}
          onProfile={() => {}}
          onAIBlog={() => {}}
          onSubUsers={() => {}}
        />
      )}
      
      {page === "admin" && (
        <AdminPanelNew 
          user={user}
          theme={theme} 
          toggleTheme={toggleTheme}
          onLogout={handleLogout}
          onBack={() => setPage("generator")}
        />
      )}
    </ThemeContext.Provider>
  );
}

export default App;
